import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  SafeAreaView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  Alert,
  StatusBar,
  Image,
  ActivityIndicator,
  useWindowDimensions,
  TextInput,
  Animated,
  Easing,
  ScrollView,
} from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import { colors, spacing, typography, chat } from '../theme';
import { designTokens } from '../theme/design-system';
import { useAppContext } from '../contexts/AppContext';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { apiService } from '../services/api';
import { useDeliveryRetry } from '../hooks/useDeliveryRetry';
import { useLocale } from '../contexts/LocaleContext';
import { createTranslator } from '../i18n';
import { useTypography } from '../hooks/useTypography';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useIsMobileBrowser } from '../hooks/useIsMobileBrowser';
import { decodeBase64Url } from '../utils/token-utils';
import { ScreenLayout } from '../components/ScreenLayout';
import { Header } from '../components/Header';
import { useApiSync } from '../hooks/useApiSync';
import { usePermissions } from '../hooks/usePermissions';
import { socketService } from '../services/socket.service';
import { MessageBubble } from '../components/MessageBubble';
import { MessageInputWithVoice } from '../components/MessageInputWithVoice';
import { AiConversationAssistant } from '../components/AiConversationAssistant';
import { ShareDocumentModal, ShareDocumentType } from '../components/ShareDocumentModal';
import { ChoiceDraftEditor, newChoice } from '../components/ChoiceDraftEditor';
import { OpenCartsCard } from '../components/OpenCartsCard';
import type { AiCommerceChoice, AiCommerceLine } from '../services/ai.types';
import { aiCustomerLanguage, buildAiChoiceMessage } from '../utils/ai-choice-message';
import { TypingIndicator } from '../components/TypingIndicator';
import { ReplyIndicator } from '../components/ReplyIndicator';
import { ProviderIndicator, getProviderFromConversation } from '../components/ProviderIndicator';
import { AvatarWithProviderBadge } from '../components/AvatarWithProviderBadge';
import { WhatsAppContactModal } from '../components/WhatsAppContactModal';
import { ReopenWithTemplateModal } from '../components/ReopenWithTemplateModal';
import { ForwardMessageModal, ForwardTargetSelection } from '../components/ForwardMessageModal';
import { UserAssignmentModal } from '../components/UserAssignmentModal';
import { Modal as AppModal } from '../components/Modal';
import { ContactEditForm } from '../components/ContactEditForm';
import { Ionicons } from '@expo/vector-icons';
import { useChatStore } from '../store/chatStore';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppStore } from '../store/appStore';
import { getTempValue, removeTempValue } from '../utils/tempStorage';
import { PANE_ANIMATION_DURATION } from '../constants/paneLayout';
import * as Clipboard from 'expo-clipboard';
import { NotesSection } from '../components/NotesSection';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { firstText } from '../utils/contactIdentifiers';


interface Message {
  _id: string;
  messageId: string;
  externalId?: string; // WhatsApp message ID for reply linking
  contactId: string;
  from: string;
  to: string;
  type: 'text' | 'image' | 'video' | 'audio' | 'document' | 'location' | 'contact' | 'sticker' | 'template' | 'interactive';
  content: string;
  mediaUrl?: string;
  mediaType?: string;
  mediaName?: string;
  mediaSize?: number;
  timestamp: string;
  direction: 'inbound' | 'outbound' | 'outbound-api';
  status: 'sent' | 'delivered' | 'read' | 'failed' | 'pending';
  statusTimestamp?: string;
  errorMessage?: string;
  errorCode?: number;
  providerId: string;
  phoneNumberId: string;
  tags: string[];
  category?: string;
  priority?: 'low' | 'normal' | 'high';
  createdAt: string;
  updatedAt: string;
  // Reply fields
  replyTo?: string;
  replyToMessage?: Message;
  // Contact fields
  contactName?: string;
  contactPhone?: string;
  // Location fields
  latitude?: number;
  longitude?: number;
  address?: string;
  city?: string;
  country?: string;
  text?: string;
  interactive?: any;
  interactiveType?: string;
  referenceType?: 'product' | 'catalog' | 'calendar' | 'order' | 'quotation';
  referenceId?: string;
}

type MessageLocationPayload = {
  latitude: number;
  longitude: number;
  address?: string;
  text?: string;
  city?: string;
  country?: string;
};

interface Contact {
  id: string;
  parentContactId?: string;
  name: string;
  phoneNumber: string;
  profilePicture?: string;
  profilePictureUrl?: string;
  location?: {
    latitude?: number;
    longitude?: number;
    address?: string;
  };
  isOnline: boolean;
  lastSeen?: string;
  isBlocked: boolean;
  tags: string[];
}

interface AssignmentRecord {
  _id: string;
  assignedTo?:
    | string
    | {
        _id: string;
        firstName?: string;
        lastName?: string;
        email?: string;
      }
    | null;
  departmentId?:
    | string
    | {
        _id: string;
        name?: string;
        description?: string;
      }
    | null;
  assignedBy?: string;
  status?: string;
  parentAssignmentId?: string;
  lockedBy?: string;
  lockedAt?: string;
  transferToUserId?: string;
  transferToDepartmentId?: string;
  createdAt?: string;
}

interface Conversation {
  _id: string;
  conversationId: string;
  contactId: string;
  parentContactId?: string;
  phoneNumber: string;
  contactName: string;
  channel: string;
  status: 'active' | 'archived' | 'closed' | 'spam';
  priority: 'low' | 'normal' | 'high';
  profilePictureUrl?: string;
  participants: Array<{
    phoneNumber: string;
    name?: string;
    isBusiness: boolean;
    isOnline?: boolean;
  }>;
  assignedTo?: string;
  lastMessageAt: string;           // Last message (any direction) - for display
  contactLastMessageAt?: string;   // Last inbound message from contact - for 24-hour window
  messageCount: number;
  unreadCount: number;
  // Set when this contact recently hit Meta's "healthy ecosystem engagement"
  // delivery throttle (error 131049) — surfaced proactively on conversation
  // open (not just at send time) so an agent sees it before attempting a
  // send that's likely to fail (and, per the 2026-09-08 incident, retrying
  // during the cooldown appears to make the suppression last even longer).
  engagementCooldown?: { onCooldown: boolean; reason?: string; lastFailedAt?: string };
}

interface ConversationScreenProps {
  conversationId: string;
  onNavigateBack: () => void;
  /** Renders inside a host screen's own pane (desktop web's persistent
   * contacts-pane shell) instead of as a full standalone screen — skips this
   * screen's own <ScreenLayout>/app-level header/pane-width-mirroring
   * entirely, and lays the contact-info bar + messages + input out as a
   * plain flex column sized to the embedding container instead of the full
   * browser viewport. Defaults false (today's existing full-screen
   * behavior, reached via navigateTo('conversation', id), is unchanged). */
  embedded?: boolean;
  /** Embedded mode only — "View Contact" (tapped from the group-actions
   * menu) has no local right pane to open in embedded mode (the embedded
   * conversation IS the content occupying the host's right pane), so it
   * bubbles the resolved contact up to the host screen instead. */
  onViewContact?: (contact: any) => void;
}

export const ConversationScreen: React.FC<ConversationScreenProps> = ({
  conversationId,
  onNavigateBack,
  embedded = false,
  onViewContact,
}) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, setActiveTab, startPhoneCall, isPhoneDialerReady } = useAppContext();
  const { phoneAccount } = useProfileCompletion();
  const { hasPermissionAtLeast } = usePermissions();
  const canManageNotes = hasPermissionAtLeast('contacts.manageNotes', 'edit');
  const canManageContactAssignments = hasPermissionAtLeast('contacts.manageAssignments', 'edit');
  const hasPhoneService = phoneAccount?.status === 'active';
  const { isRTL, locale } = useLocale();
  const t = createTranslator(locale);
  const typo = useTypography();
  const rtl = useRTLStyles();
  // Reactive to resize/rotation — the old module-scope Dimensions snapshot
  // never updated, so a window narrowed past the breakpoint (or a phone
  // browser) kept desktop pane offsets on the fixed header/input bar.
  const isMobileBrowser = useIsMobileBrowser();
  const { width: screenWidth } = useWindowDimensions();
  const [messages, setMessages] = useState<Message[]>([]);
  const [composerText, setComposerText] = useState('');
  // Bumped on every successful send: the assistant's suggestion for that customer message is used up.
  const [sentVersion, setSentVersion] = useState(0);
  // "Send quotation / order" from the + menu: which picker is open, and for which customer.
  const [shareType, setShareType] = useState<ShareDocumentType | null>(null);
  const [shareContactId, setShareContactId] = useState<string | null>(null);
  const [pendingAiChoices, setPendingAiChoices] = useState<AiCommerceChoice[]>([]);
  const [aiTemplateId, setAiTemplateId] = useState<string | undefined>();
  useEffect(() => { setComposerText(''); setPendingAiChoices([]); setAiTemplateId(undefined); }, [conversationId]);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const deliveryRetry = useDeliveryRetry(conversationId, conversation?.channel === 'whatsapp');
  const showDeliveryRetry = !!(deliveryRetry.retry?.enabled || deliveryRetry.retry?.state === 'needs_review');
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [isAtTop, setIsAtTop] = useState(false);
  const [lastLoadTrigger, setLastLoadTrigger] = useState(0);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUser, setTypingUser] = useState<string>('');
  const [showMediaOptions, setShowMediaOptions] = useState(false);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [starredMessages, setStarredMessages] = useState<Set<string>>(new Set());
  const [showWhatsAppContactModal, setShowWhatsAppContactModal] = useState(false);
  const [showReopenTemplateModal, setShowReopenTemplateModal] = useState(false);
  // Resolved via getContactDialTarget right before opening a modal that actually SENDS
  // a message (ReopenWithTemplateModal / WhatsAppContactModal) — contact?.phoneNumber and
  // conversation?.phoneNumber are BOTH sourced from getConversation()/getUnifiedConversations(),
  // which deliberately mask the number for display when the caller lacks
  // contacts.viewMobileNumber (see omni-channel.service.ts's getConversation — phoneFallback
  // is masked via maskPhoneValue when !canSeeNumber). Confirmed real production bug: sending a
  // template through this modal used that masked value as the actual WhatsApp API destination,
  // rejected by Meta as "The parameter to is required" once stripped of non-digits. Never read
  // contact/conversation phoneNumber directly for a send.
  const [resolvedReopenPhone, setResolvedReopenPhone] = useState<string>('');
  const [showFacebookReopenModal, setShowFacebookReopenModal] = useState(false);
  const [facebookReopenMessage, setFacebookReopenMessage] = useState('');
  const [sendingFacebookReopen, setSendingFacebookReopen] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [messageToForward, setMessageToForward] = useState<Message | null>(null);
  const [isForwarding, setIsForwarding] = useState(false);
  const [assignmentRecords, setAssignmentRecords] = useState<AssignmentRecord[]>([]);
  const [currentAssignment, setCurrentAssignment] = useState<AssignmentRecord | null>(null);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentActionLoading, setAssignmentActionLoading] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [tokenUserId, setTokenUserId] = useState<string | null>(null);

  // Extract user role from token on component mount
  useEffect(() => {
    const getUserRole = () => {
      try {
        const token = apiService.getAuthToken();
        if (token) {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3 && tokenParts[1]) {
            const payload = JSON.parse(decodeBase64Url(tokenParts[1]));
            const role = payload.businessRole || payload.business?.role || payload.userRole || payload.role;
            setUserRole(role);
            const userIdFromToken = payload.userId || payload.id || payload._id || null;
            if (userIdFromToken) {
              setTokenUserId(userIdFromToken);
            }
          }
        }
      } catch (error) {
        console.error('❌ [ConversationScreen] Error extracting user role from token:', error);
      }
    };
    getUserRole();
  }, []);
  const [showAssignmentActionsMenu, setShowAssignmentActionsMenu] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentMenuAnchor, setAssignmentMenuAnchor] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const assignmentActionButtonRef = useRef<View>(null);
  // Header "group actions" menu (View Contact / Copy Number / Assign To / Notes) —
  // replaces the old single Assign To/Actions header button.
  const [showGroupActionsMenu, setShowGroupActionsMenu] = useState(false);
  const [groupActionsMenuAnchor, setGroupActionsMenuAnchor] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const groupActionsButtonRef = useRef<View>(null);
  // Phone picker — same rationale as ContactsList's call icon: a contact can
  // have more than one saved phone number (identifiers.phone[]), so before
  // dialing we check the full parent contact and, if there's more than one
  // unique number, let the user choose instead of silently using the
  // server's default priority.
  const [phonePickerNumbers, setPhonePickerNumbers] = useState<Array<{ value: string; index: number }> | null>(null);
  const [phonePickerContactId, setPhonePickerContactId] = useState<string | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [contactNotes, setContactNotes] = useState<any[]>([]);
  const [newNoteContent, setNewNoteContent] = useState('');
  const notesScrollRef = useRef<ScrollView>(null);
  const notesInputRef = useRef<TextInput>(null);
  const [loadingNotes, setLoadingNotes] = useState(false);
  // Header "new notes" indicator: true when the contact has notes the current user
  // hasn't opened the Notes modal for yet. Seen-count is persisted per contact so it
  // survives leaving and re-entering the conversation.
  const [hasUnseenNotes, setHasUnseenNotes] = useState(false);
  const [tempParentContactId, setTempParentContactId] = useState<string | null>(null);
  const [messagesPaneWidth, setMessagesPaneWidth] = useState<number | null>(null);
  const flatListRef = useRef<FlatList>(null);
  const { currentBusiness, user, leftPaneCollapsed, leftPaneWidth, rightPaneCollapsed, rightPaneWidth, bumpContactsRefreshToken } = useAppStore();
  const assistantScope = `${currentBusiness?._id}:${conversationId}`;
  const assistantScopeRef = useRef(assistantScope);
  assistantScopeRef.current = assistantScope;

  const messagesRef = useRef<Message[]>(messages);
  const currentPageRef = useRef<number>(currentPage);
  const hasMoreMessagesRef = useRef<boolean>(hasMoreMessages);
  const pendingScrollTargetRef = useRef<string | null>(null);
  const isFetchingForScrollRef = useRef<boolean>(false);
  const scrollLoadAttemptsRef = useRef<number>(0);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const highlightedMessageIdRef = useRef<string | null>(highlightedMessageId);
  const hasScrolledToEndOnLoadRef = useRef<boolean>(false);
  const shouldScrollToBottomRef = useRef<boolean>(false);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    hasMoreMessagesRef.current = hasMoreMessages;
  }, [hasMoreMessages]);
  
  useEffect(() => {
    highlightedMessageIdRef.current = highlightedMessageId;
  }, [highlightedMessageId]);

  const scrollToBottom = useCallback((animated: boolean = true) => {
    if (!flatListRef.current) return;
    flatListRef.current.scrollToOffset({ offset: 0, animated });
  }, []);
  
  const upsertMessage = useCallback((incoming: Message) => {
    setMessages(prev => {
      let index = prev.findIndex(msg =>
        (msg.messageId && incoming.messageId && msg.messageId === incoming.messageId) ||
        (msg.externalId && incoming.externalId && msg.externalId === incoming.externalId) ||
        msg._id === incoming._id
      );
      // Our own message can come back over the socket before the send request returns:
      // it then belongs to the optimistic placeholder, not a new bubble.
      if (index < 0 && incoming.direction !== 'inbound') {
        index = prev.findIndex(msg => String(msg._id).startsWith('temp_') && msg.type === incoming.type
          && (msg.type !== 'text' || (msg.content || '').trim() === (incoming.content || '').trim()));
      }

      if (index >= 0) {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          ...incoming,
          status: incoming.status || updated[index].status,
          timestamp: incoming.timestamp || updated[index].timestamp,
          updatedAt: incoming.updatedAt || updated[index].updatedAt,
          mediaUrl: incoming.mediaUrl || updated[index].mediaUrl,
          mediaType: incoming.mediaType || updated[index].mediaType,
          content: incoming.content ?? updated[index].content,
        };
        return updated;
      }

      const next = [...prev, incoming];
      next.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      return next;
    });

    setConversation(prev =>
      prev
        ? {
            ...prev,
            lastMessageAt: incoming.timestamp || prev.lastMessageAt,
            contactLastMessageAt:
              incoming.direction === 'inbound'
                ? incoming.timestamp || prev.contactLastMessageAt
                : prev.contactLastMessageAt,
          }
        : prev
    );
  }, [setMessages, setConversation]);
  
  // Right pane states
  const [showRightPane] = useState(true);
  const [showLeftPane] = useState(true);
  // 'contacts' (and the never-actually-set 'details') removed — the contacts
  // list now lives in the left pane (leftPaneContent="contacts"); this pane
  // is only ever used for 'edit-contact'.
  const [rightPaneContent, setRightPaneContent] = useState<'edit-contact' | 'empty'>('empty');
  // Full omni-contact record fetched on demand when "View Contact" is chosen from
  // the header's group-actions menu — separate from `contact` (this screen's own
  // minimal channel-contact shape) since ContactEditForm needs the full parent
  // contact record to edit.
  const [viewedContact, setViewedContact] = useState<any | null>(null);
  const [loadingViewedContact, setLoadingViewedContact] = useState(false);
  // 0 when embedded — this screen isn't a full-width page with its own side
  // panes to inset for in that mode; it's already confined to the host's pane.
  const paneHorizontalInset = !embedded && Platform.OS === 'web' && !isMobileBrowser && showRightPane && !rightPaneCollapsed ? rightPaneWidth : 0;
  const isWebDesktop = Platform.OS === 'web' && !isMobileBrowser;
  // This screen's own position:fixed header/input bar sit outside ScreenLayout's
  // `children` tree, so they don't get its animated contentArea margins for free —
  // these run a parallel Animated.Value pair off the SAME store fields/duration/easing
  // as ScreenLayout's leftMarginAnim/rightMarginAnim, so collapsing/expanding a pane
  // slides these in lockstep with the pane and the rest of the middle content instead
  // of snapping instantly while everything else animates.
  // Embedded mode: this screen renders inside the HOST's own pane, which
  // already manages its own position — there's no separate fixed header/
  // input of this screen's own to keep in sync with the app-level pane
  // widths. Pinning these at a constant 0 (and skipping the effects that
  // would otherwise drive them from the store) makes every consumer of the
  // derived physicalLeftInsetAnim/physicalRightInsetAnim below (header,
  // input, the Notes modal card's margins) naturally resolve to 0 with no
  // per-call-site special-casing needed.
  const leftPaneAnim = useRef(new Animated.Value(embedded || !isWebDesktop ? 0 : (showLeftPane && !leftPaneCollapsed ? leftPaneWidth : 0))).current;
  const rightPaneAnim = useRef(new Animated.Value(embedded || !isWebDesktop ? 0 : (showRightPane && !rightPaneCollapsed ? rightPaneWidth : 0))).current;

  // Not skipped on mobile/native: the panes aren't rendered there, so the
  // fixed header/input must sit at 0 — including after a desktop window is
  // narrowed past the breakpoint with panes previously open.
  useEffect(() => {
    if (embedded) return;
    Animated.timing(leftPaneAnim, {
      toValue: isWebDesktop && showLeftPane && !leftPaneCollapsed ? leftPaneWidth : 0,
      duration: PANE_ANIMATION_DURATION,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [embedded, showLeftPane, leftPaneCollapsed, leftPaneWidth, isWebDesktop, leftPaneAnim]);

  useEffect(() => {
    if (embedded) return;
    Animated.timing(rightPaneAnim, {
      toValue: isWebDesktop && showRightPane && !rightPaneCollapsed ? rightPaneWidth : 0,
      duration: PANE_ANIMATION_DURATION,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [embedded, showRightPane, rightPaneCollapsed, rightPaneWidth, isWebDesktop, rightPaneAnim]);

  // LeftPane sits on the physical right edge in RTL, RightPane on the physical left —
  // matches ScreenLayout's own contentArea margin logic exactly.
  const physicalLeftInsetAnim = isRTL ? rightPaneAnim : leftPaneAnim;
  const physicalRightInsetAnim = isRTL ? leftPaneAnim : rightPaneAnim;
  // ScreenLayout measures the real rendered height of the `header` prop we pass it
  // (it varies with content — e.g. the desktop nav row) and reports it back here so
  // this screen's own fixed header/input bar can align with it exactly instead of
  // guessing a static value that drifts and either overlaps the app header or leaves
  // a gap under it.
  const [appHeaderHeight, setAppHeaderHeight] = useState(84);
  
  // Get phone number for modal
  const getPhoneNumberForModal = () => {
    // Check contact phone FIRST (more reliable), then conversation phone
    const contactPhone = contact?.phoneNumber || '';
    const conversationPhone = conversation?.phoneNumber || '';
    
    console.log('🔍 [ConversationScreen] getPhoneNumberForModal:', {
      conversationPhone,
      contactPhone,
      checkingContactFirst: true
    });
    
    // Try contact phone first (filter out invalid values)
    if (contactPhone && !contactPhone.match(/^(system|business|unknown)/i)) {
      // Ensure phone has + prefix
      const formattedPhone = contactPhone.startsWith('+') ? contactPhone : `+${contactPhone}`;
      console.log('✅ [ConversationScreen] Using contact phone:', formattedPhone);
      return formattedPhone;
    }
    
    // Fallback to conversation phone
    if (conversationPhone && !conversationPhone.match(/^(system|business|unknown)/i)) {
      // Ensure phone has + prefix
      const formattedPhone = conversationPhone.startsWith('+') ? conversationPhone : `+${conversationPhone}`;
      console.log('✅ [ConversationScreen] Using conversation phone:', formattedPhone);
      return formattedPhone;
    }
    
    console.log('❌ [ConversationScreen] No valid phone number found');
    return '';
  };

  const { showNotification } = useAppContext();
  const { tokens } = useApiSync();
  const { markMessagesAsRead: markAsReadInStore } = useChatStore();
  const insets = useSafeAreaInsets();
  const keyboardOffset = Platform.OS === 'ios' ? insets.top + 80 : 0;
  const currentUserId = user?.id || user?._id || tokenUserId || '';
  const lastAssignmentLookupRef = useRef<string | null>(null);

  const getAssignmentIdValue = (
    value: AssignmentRecord['assignedTo'] | AssignmentRecord['departmentId']
  ) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return (value as any)._id || (value as any).id || null;
  };

  const resolveActiveAssignment = useCallback((records: AssignmentRecord[]) => {
    // Load all assignments except cancelled
    const validRecords = records.filter(r => r.status !== 'cancelled');

    const userAssignment = validRecords.find(r => {
      const assignedId = getAssignmentIdValue(r.assignedTo);
      return assignedId && assignedId === currentUserId;
    });

    if (userAssignment) return userAssignment;

    const departmentAssignment = validRecords.find(r => !!getAssignmentIdValue(r.departmentId)) || null;
    if (departmentAssignment) return departmentAssignment;

    return validRecords[0] || null;
  }, [currentUserId]);

  const fetchAssignmentsForTarget = useCallback(async (targetId: string, onlyOmni = false) => {
    console.log('🔍 [Assignment Debug] Fetching for targetId:', targetId, 'onlyOmni:', onlyOmni);
    
    const cacheBuster = Date.now();
    const omniUrl = `/api/assignments/target/omni_contact/${targetId}?t=${cacheBuster}`;
    console.log('🔍 [Assignment Debug] Trying:', omniUrl);
    const omniResponse = await apiService.authenticatedRequest(
      omniUrl,
      { method: 'GET' }
    );

    let responseData = (omniResponse as any).data ?? (omniResponse as any).Data;
    console.log('🔍 [Assignment Debug] omni_contact response:', responseData);
    if (Array.isArray(responseData) && responseData.length > 0) {
      console.log('✅ [Assignment Debug] Found', responseData.length, 'assignments for omni_contact');
      return responseData as AssignmentRecord[];
    }

    if (onlyOmni) {
      return [];
    }

    const whatsappUrl = `/api/assignments/target/whatsapp_contact/${targetId}?t=${cacheBuster}`;
    console.log('🔍 [Assignment Debug] Trying:', whatsappUrl);
    const whatsappResponse = await apiService.authenticatedRequest(
      whatsappUrl,
      { method: 'GET' }
    );
    responseData = (whatsappResponse as any).data ?? (whatsappResponse as any).Data;
    console.log('🔍 [Assignment Debug] whatsapp_contact response:', responseData);
    if (Array.isArray(responseData) && responseData.length > 0) {
      console.log('✅ [Assignment Debug] Found', responseData.length, 'assignments for whatsapp_contact');
      return responseData as AssignmentRecord[];
    }

    const facebookUrl = `/api/assignments/target/facebook_contact/${targetId}?t=${cacheBuster}`;
    console.log('🔍 [Assignment Debug] Trying:', facebookUrl);
    const facebookResponse = await apiService.authenticatedRequest(
      facebookUrl,
      { method: 'GET' }
    );
    responseData = (facebookResponse as any).data ?? (facebookResponse as any).Data;
    console.log('🔍 [Assignment Debug] facebook_contact response:', responseData);
    if (Array.isArray(responseData) && responseData.length > 0) {
      console.log('✅ [Assignment Debug] Found', responseData.length, 'assignments for facebook_contact');
    }
    return Array.isArray(responseData) ? (responseData as AssignmentRecord[]) : [];
  }, []);

  const fetchAssignmentsForConversation = useCallback(async (conversationIdValue: string) => {
    const channel = conversation?.channel || '';
    const conversationTargetType = channel === 'facebook-messenger'
      ? 'facebook_conversation'
      : 'whatsapp_conversation';

    const cacheBuster = Date.now();
    const response = await apiService.authenticatedRequest(
      `/api/assignments/target/${conversationTargetType}/${conversationIdValue}?t=${cacheBuster}`,
      { method: 'GET' }
    );
    const responseData = (response as any).data ?? (response as any).Data;
    return Array.isArray(responseData) ? (responseData as AssignmentRecord[]) : [];
  }, [conversation?.channel]);

  const loadAssignments = useCallback(async (targetId: string, force = false) => {
    if (assignmentsLoading && !force) {
      return;
    }
    setAssignmentsLoading(true);
    
    try {
      const candidateIds = [
        targetId,
        (conversation as any)?.parentContactId,
        (contact as any)?.parentContactId
      ].filter(Boolean) as string[];

      const lookupKey = candidateIds.join('|') + `|${conversation?.conversationId || conversationId}`;
      if (!force && lastAssignmentLookupRef.current === lookupKey) {
        setAssignmentsLoading(false);
        return;
      }
      lastAssignmentLookupRef.current = lookupKey;

      const parentId = (conversation as any)?.parentContactId || (contact as any)?.parentContactId || null;

      if (parentId) {
        const parentRecords = await fetchAssignmentsForTarget(parentId, true);
        if (parentRecords.length > 0) {
          setAssignmentRecords(parentRecords);
          setCurrentAssignment(resolveActiveAssignment(parentRecords));
          return;
        }
      }

      // Try to find assignments from the candidate IDs
      for (const candidateId of candidateIds) {
        const records = await fetchAssignmentsForTarget(candidateId);
        if (records.length > 0) {
          setAssignmentRecords(records);
          setCurrentAssignment(resolveActiveAssignment(records));
          return;
        }
      }

      // If not found by contact, try conversation lookup
      const conversationIdValue = conversation?.conversationId || conversationId;
      if (conversationIdValue) {
        const conversationRecords = await fetchAssignmentsForConversation(conversationIdValue);
        if (conversationRecords.length > 0) {
          setAssignmentRecords(conversationRecords);
          setCurrentAssignment(resolveActiveAssignment(conversationRecords));
          return;
        }
      }

      setAssignmentRecords([]);
      setCurrentAssignment(null);
    } catch (error) {
      console.error('❌ [ConversationScreen] Error loading assignments:', error);
    } finally {
      setAssignmentsLoading(false);
    }
  }, [contact, conversation, conversationId, fetchAssignmentsForConversation, fetchAssignmentsForTarget, resolveActiveAssignment]);

  const activeAssignment = currentAssignment || assignmentRecords[0] || null;

  const isDepartmentAssignment = useMemo(() => {
    if (!activeAssignment) return false;
    const departmentId = getAssignmentIdValue(activeAssignment.departmentId);
    const assignedToId = getAssignmentIdValue(activeAssignment.assignedTo);
    return !!departmentId && !assignedToId;
  }, [activeAssignment]);

  const isUserAssignment = useMemo(() => {
    if (!activeAssignment) return false;
    const assignedToId = getAssignmentIdValue(activeAssignment.assignedTo);
    const departmentId = getAssignmentIdValue(activeAssignment.departmentId);
    return !!assignedToId && !departmentId;
  }, [activeAssignment]);

  const isCurrentUserAssignee = useMemo(() => {
    if (!activeAssignment) return false;
    return getAssignmentIdValue(activeAssignment.assignedTo) === currentUserId;
  }, [activeAssignment, currentUserId]);

  const refreshAssignments = useCallback(async () => {
    const targetId = conversation?.contactId || contact?.id;
    if (targetId) {
      await loadAssignments(targetId, true);
    }
  }, [conversation?.contactId, contact?.id, loadAssignments]);

  const getActionAssignment = useCallback(async () => {
    if (activeAssignment) return activeAssignment;

    const candidateIds = [
      conversation?.contactId,
      contact?.id,
      (conversation as any)?.parentContactId,
      (contact as any)?.parentContactId
    ].filter(Boolean) as string[];

    for (const candidateId of candidateIds) {
      const records = await fetchAssignmentsForTarget(candidateId);
      if (records.length > 0) {
        setAssignmentRecords(records);
        const resolved = resolveActiveAssignment(records);
        setCurrentAssignment(resolved);
        return resolved;
      }
    }

    const conversationIdValue = conversation?.conversationId || conversationId;
    if (conversationIdValue) {
      const records = await fetchAssignmentsForConversation(conversationIdValue);
      if (records.length > 0) {
        setAssignmentRecords(records);
        const resolved = resolveActiveAssignment(records);
        setCurrentAssignment(resolved);
        return resolved;
      }
    }

    const identifier = contact?.phoneNumber || conversation?.phoneNumber;
    if (identifier) {
      try {
        const query: { phone?: string; whatsapp?: string; 'facebook-messenger'?: string } = {
          phone: identifier
        };
        if (conversation?.channel === 'whatsapp') {
          query.whatsapp = identifier;
        } else if (conversation?.channel === 'facebook-messenger') {
          query['facebook-messenger'] = identifier;
        }

        const parentResponse = await apiService.searchParentContactsByIdentifiers(query);
        const parentData = (parentResponse as any).data ?? (parentResponse as any).Data;
        const parentContacts = Array.isArray(parentData) ? parentData : [];
        const parentContact = parentContacts[0];
        const parentId = parentContact?._id || parentContact?.id;

        if (parentId) {
          const records = await fetchAssignmentsForTarget(parentId);
          if (records.length > 0) {
            setAssignmentRecords(records);
            const resolved = resolveActiveAssignment(records);
            setCurrentAssignment(resolved);
            return resolved;
          }
        }
      } catch (error) {
        console.error('❌ [ConversationScreen] Failed to resolve parent contact for assignment:', error);
      }
    }

    return null;
  }, [activeAssignment, contact, conversation, conversationId, fetchAssignmentsForConversation, fetchAssignmentsForTarget, resolveActiveAssignment]);

  const handleStartAssignment = useCallback(async () => {
    if (assignmentActionLoading) return;
    const assignment = await getActionAssignment();
    if (!assignment) return;
    try {
      setAssignmentActionLoading(true);
      // Always use the start endpoint, regardless of assignment type
      await apiService.authenticatedRequest(`/api/assignments/${assignment._id}/start`, {
        method: 'POST'
      });
      showNotification('success', t('assignment.started'), t('assignment.startedDesc'));
      await refreshAssignments();
    } catch (error: any) {
      console.error('❌ [ConversationScreen] Failed to start assignment:', error);
      showNotification('error', t('assignment.startFailed'), error?.message || t('assignment.startFailedDesc'));
    } finally {
      setAssignmentActionLoading(false);
    }
  }, [assignmentActionLoading, getActionAssignment, refreshAssignments, showNotification]);

  const handleTransferSelection = useCallback(async (selection: { type: 'user' | 'department'; id: string }) => {
    if (assignmentActionLoading) return;
    const assignment = await getActionAssignment();
    if (!assignment) return;
    try {
      setShowTransferModal(false);
      setAssignmentActionLoading(true);

      await apiService.authenticatedRequest(`/api/assignments/${assignment._id}/transfer-with-new`, {
        method: 'POST',
        body: JSON.stringify({
          transferType: selection.type,
          transferId: selection.id
        })
      });

      showNotification('success', t('assignment.transferred'), t('assignment.transferredDesc'));
      await refreshAssignments();
    } catch (error: any) {
      console.error('❌ [ConversationScreen] Failed to transfer assignment:', error);
      showNotification('error', t('assignment.transferFailed'), error?.message || t('assignment.transferFailedDesc'));
    } finally {
      setAssignmentActionLoading(false);
    }
  }, [assignmentActionLoading, getActionAssignment, refreshAssignments, showNotification]);

  const handleCompleteAssignment = useCallback(async () => {
    if (assignmentActionLoading) return;
    const assignment = await getActionAssignment();
    if (!assignment) return;
    try {
      setAssignmentActionLoading(true);
      await apiService.authenticatedRequest(`/api/assignments/${assignment._id}/complete`, {
        method: 'POST',
        body: JSON.stringify({ completionNotes: '' })
      });
      showNotification('success', t('assignment.completed'), t('assignment.completedDesc'));
      navigateTo('chat');
      await refreshAssignments();
    } catch (error: any) {
      console.error('❌ [ConversationScreen] Failed to complete assignment:', error);
      showNotification('error', t('assignment.completeFailed'), error?.message || t('assignment.completeFailedDesc'));
    } finally {
      setAssignmentActionLoading(false);
    }
  }, [assignmentActionLoading, getActionAssignment, refreshAssignments, showNotification]);

  const handleCancelAssignment = useCallback(async () => {
    if (assignmentActionLoading) return;
    const assignment = await getActionAssignment();
    if (!assignment) return;
    try {
      setAssignmentActionLoading(true);
      await apiService.authenticatedRequest(`/api/assignments/${assignment._id}/cancel`, {
        method: 'POST'
      });
      showNotification('success', t('assignment.cancelled'), t('assignment.cancelledDesc'));
      navigateTo('chat');
      await refreshAssignments();
    } catch (error: any) {
      console.error('❌ [ConversationScreen] Failed to cancel assignment:', error);
      showNotification('error', t('assignment.cancelFailed'), error?.message || t('assignment.cancelFailedDesc'));
    } finally {
      setAssignmentActionLoading(false);
    }
  }, [assignmentActionLoading, getActionAssignment, refreshAssignments, showNotification]);

  const isAdminOrOwner = userRole === 'owner' || userRole === 'admin';

  const canStartAssignment = useMemo(() => {
    if (isAdminOrOwner && !isCurrentUserAssignee) return false;
    // Disable Start button if assignment is already in progress
    if (currentAssignment?.status === 'inProgress') return false;
    return isUserAssignment || isDepartmentAssignment;
  }, [isAdminOrOwner, isCurrentUserAssignee, isUserAssignment, isDepartmentAssignment, currentAssignment?.status]);

  const canTransferAssignment = useMemo(() => {
    if (isAdminOrOwner && !isCurrentUserAssignee) return false;
    return isUserAssignment;
  }, [isAdminOrOwner, isCurrentUserAssignee, isUserAssignment]);

  const canCompleteAssignment = useMemo(() => {
    if (isAdminOrOwner && !isCurrentUserAssignee) return false;
    return isUserAssignment;
  }, [isAdminOrOwner, isCurrentUserAssignee, isUserAssignment]);

  const canCancelAssignment = useMemo(() => {
    if (isAdminOrOwner && !isCurrentUserAssignee) return false;
    return isUserAssignment;
  }, [isAdminOrOwner, isCurrentUserAssignee, isUserAssignment]);

  const hasAssignment = !!currentAssignment || assignmentRecords.length > 0;

  const openAssignmentMenu = useCallback(() => {
    const button = assignmentActionButtonRef.current as any;
    if (button?.measureInWindow) {
      button.measureInWindow((x: number, y: number, width: number, height: number) => {
        setAssignmentMenuAnchor({ x, y, width, height });
        setShowAssignmentActionsMenu(true);
      });
      return;
    }

    setShowAssignmentActionsMenu(true);
  }, []);

  const openGroupActionsMenu = useCallback(() => {
    const button = groupActionsButtonRef.current as any;
    if (button?.measureInWindow) {
      button.measureInWindow((x: number, y: number, width: number, height: number) => {
        setGroupActionsMenuAnchor({ x, y, width, height });
        setShowGroupActionsMenu(true);
      });
      return;
    }
    setShowGroupActionsMenu(true);
  }, []);

  // `parentContactId` isn't always present on older/never-linked conversations —
  // falls back to searching the omni-contact index by phone/whatsapp identifier,
  // the same resolution strategy already used by loadAssignments/getActionAssignment
  // above. Cached per phone number so View Contact/Notes don't re-search on every open.
  const resolvedParentContactIdRef = useRef<Record<string, string | null>>({});
  const resolveParentContactId = useCallback(async (): Promise<string | null> => {
    const direct = (conversation as any)?.parentContactId || (contact as any)?.parentContactId;
    if (direct) return direct;

    const identifier = contact?.phoneNumber || conversation?.phoneNumber;
    if (!identifier) return null;

    if (identifier in resolvedParentContactIdRef.current) {
      return resolvedParentContactIdRef.current[identifier];
    }

    try {
      const query: { phone?: string; whatsapp?: string; 'facebook-messenger'?: string } = { phone: identifier };
      if (conversation?.channel === 'whatsapp') query.whatsapp = identifier;
      else if (conversation?.channel === 'facebook-messenger') query['facebook-messenger'] = identifier;

      const parentResponse = await apiService.searchParentContactsByIdentifiers(query);
      const parentData = (parentResponse as any).data ?? (parentResponse as any).Data;
      const parentContacts = Array.isArray(parentData) ? parentData : [];
      const parentId = parentContacts[0]?._id || parentContacts[0]?.id || null;
      resolvedParentContactIdRef.current[identifier] = parentId;
      return parentId;
    } catch (error) {
      console.error('Failed to resolve parent contact:', error);
      return null;
    }
  }, [conversation, contact]);

  // Resolves the real, unmasked phone number for the current contact before opening a
  // modal that will actually SEND a message (ReopenWithTemplateModal / WhatsAppContactModal),
  // as opposed to display. contact?.phoneNumber / conversation?.phoneNumber both come from
  // getConversation()/getUnifiedConversations(), which mask the number for users lacking
  // contacts.viewMobileNumber — safe to display, unsafe to send to.
  //
  // getContactDialTarget requires the OMNI-CONTACT (parent) id, not conversation.contactId /
  // contact.id — those are the WhatsApp-channel-specific contact id and 404 against
  // OmniContactService.findById. Confirmed via production log: passing the wrong id made
  // dial-target resolution fail silently, falling through to the masked value below, which
  // then got digit-stripped by the backend's normalizePhoneNumber down to just its last 4
  // visible digits (e.g. "+********6631" -> "6631"), which Meta then rejected as malformed
  // (error 131009). resolveParentContactId is the same helper View Contact/Notes already use.
  const resolvePhoneForSend = useCallback(async (): Promise<string> => {
    const parentContactId = await resolveParentContactId();
    if (parentContactId) {
      try {
        // Passing conversationId (this screen's OWN conversation, not the contact's
        // most-recently-linked one) is critical for a contact with more than one
        // linked WhatsApp number — without it, dial-target falls back to
        // profile.phone, which reflects whichever number was linked most recently
        // REGARDLESS of which conversation is actually open. Confirmed real
        // production bug: sending from conversation B's screen for a contact with
        // conversations A and B silently sent via A's number instead.
        const res = await apiService.getContactDialTarget(parentContactId, 'whatsapp', undefined, conversationId);
        const resolved = res.data?.phoneNumber;
        if (resolved) {
          return resolved.startsWith('+') ? resolved : `+${resolved}`;
        }
      } catch (error) {
        console.error('[ConversationScreen] Failed to resolve dial target for contact', parentContactId, error);
      }
    }
    // Fall back to the (possibly masked) display value rather than blocking the modal outright —
    // ReopenWithTemplateModal/WhatsAppContactModal will surface a send-time error if it's unusable.
    return getPhoneNumberForModal();
  }, [resolveParentContactId, conversationId]);

  // The assistant's recommended message, sent as is: with its list/buttons when it has choices.
  const sendAiRecommendation = async (text: string, choices: AiCommerceChoice[]) => {
    if (!conversation || sending) return;
    if (!choices.length) return sendMessage(text, 'text');
    const packet = buildAiChoiceMessage(conversation.channel, text, choices, aiCustomerLanguage(messages, text));
    return sendMessage(packet.content, packet.type, undefined, undefined, undefined, undefined,
      { aiChoiceDraft: true, ...(packet.interactive ? { interactive: packet.interactive, interactiveType: packet.interactive.type } : {}) });
  };

  const openShareDocument = async (type: ShareDocumentType) => {
    const scope = assistantScope;
    const parentId = await resolveParentContactId().catch(() => null);
    if (assistantScopeRef.current !== scope) return;
    setShareContactId(parentId || null);
    setShareType(type);
  };
  // The customer's language: their latest message with words in it.
  const customerLanguage = (): 'ar' | 'en' => {
    const latest = messages.find(m => m.direction === 'inbound' && /\p{L}/u.test(m.content || ''));
    return latest && !/[\u0600-\u06FF]/.test(latest.content || '') ? 'en' : 'ar';
  };

  const prepareAiTemplate = async (templateId: string) => {
    const scope = assistantScope;
    const phone = await resolvePhoneForSend();
    if (assistantScopeRef.current !== scope) return;
    setAiTemplateId(templateId); setResolvedReopenPhone(phone); setShowReopenTemplateModal(true);
  };

  const prepareAiDocument = async (type: 'order' | 'quotation', lines: AiCommerceLine[]) => {
    const scope = assistantScope;
    const parentId = await resolveParentContactId();
    const response = parentId ? await apiService.getParentContactById(parentId) : null;
    if (assistantScopeRef.current !== scope) return;
    const parent = response?.statusCode === 1000 ? response.data : null;
    const phone = parent?.profile?.phone || firstText(parent?.identifiers?.phone) || firstText(parent?.identifiers?.whatsapp) || '';
    const prefill = { source: 'ai-conversation', contactId: parent?._id,
      customer: { id: parent?._id, name: parent?.profile?.name || conversation?.contactName || '', phone: typeof phone === 'string' && !phone.includes('*') ? phone : '' },
      aiProductIds: lines.map(line => line.productId), aiLines: lines };
    navigateTo(type === 'order' ? 'order-add' : 'quotation-create', undefined,
      type === 'order' ? { prefillOrder: prefill } : { prefillQuotation: prefill });
  };

  // WhatsAppContactModal also sends (auto-reopens the window with a template) — resolve the
  // real number whenever it opens, same reasoning as the "reopen with template" button below.
  useEffect(() => {
    if (showWhatsAppContactModal) {
      resolvePhoneForSend().then(setResolvedReopenPhone);
    }
  }, [showWhatsAppContactModal, resolvePhoneForSend]);

  const handleViewContact = useCallback(async () => {
    setShowGroupActionsMenu(false);
    setLoadingViewedContact(true);
    try {
      const parentContactId = await resolveParentContactId();
      if (!parentContactId) {
        showNotification('error', t('common.error'), t('conversation.noContactToView'));
        return;
      }
      const result = await apiService.getParentContactById(parentContactId);
      if (result.statusCode === 1000 && result.data) {
        const parentContact = result.data;
        // ContactEditForm reads a `Contact`-shaped object with the raw omni-contact
        // nested under `.parentContact` (same shape ContactsList.tsx builds for its
        // own edit-contact flow) — the raw API response alone isn't enough, its top-
        // level fields (name/phoneNumber/email) don't exist on the omni-contact record.
        let phoneValue = parentContact.profile?.phone;
        let emailValue = parentContact.profile?.email;
        if (!phoneValue && parentContact.identifiers) {
          phoneValue = Array.isArray(parentContact.identifiers)
            ? parentContact.identifiers.find((id: any) => id.type === 'phone' || id.type === 'whatsapp')?.value
            : firstText(parentContact.identifiers.phone) || firstText(parentContact.identifiers.whatsapp);
        }
        if (!emailValue && parentContact.identifiers) {
          emailValue = Array.isArray(parentContact.identifiers)
            ? parentContact.identifiers.find((id: any) => id.type === 'email')?.value
            : firstText(parentContact.identifiers.email);
        }
        const resolvedContact = {
          id: parentContact._id || parentContact.id,
          name: parentContact.profile?.name || '',
          channel: conversation?.channel || 'whatsapp',
          phoneNumber: phoneValue,
          email: emailValue,
          profilePictureUrl: parentContact.profile?.avatar,
          parentContact,
        };
        if (embedded) {
          // No local right pane to open in embedded mode — the host screen
          // already has this exact contact's profile open in its own middle
          // pane (that's how the embedded conversation got here), so it can
          // simply re-focus it rather than this screen managing its own.
          onViewContact?.(resolvedContact);
        } else {
          setViewedContact(resolvedContact);
          setRightPaneContent('edit-contact');
        }
      } else {
        showNotification('error', t('common.error'), t('conversation.loadContactFailed'));
      }
    } catch (error: any) {
      showNotification('error', t('common.error'), error?.message || t('conversation.loadContactFailed'));
    } finally {
      setLoadingViewedContact(false);
    }
  }, [resolveParentContactId, conversation, showNotification, t, embedded, onViewContact]);

  const handleCopyNumber = useCallback(async () => {
    setShowGroupActionsMenu(false);
    const phoneNumber = getPhoneNumber();
    if (!phoneNumber) return;
    try {
      if (Platform.OS === 'web' && navigator.clipboard) {
        await navigator.clipboard.writeText(phoneNumber);
      } else {
        await Clipboard.setStringAsync(phoneNumber);
      }
      showNotification('success', t('conversation.copiedTitle'), t('conversation.numberCopied'));
    } catch (error) {
      showNotification('error', t('common.error'), t('conversation.copyFailed'));
    }
  }, [showNotification, t]);

  // Places the call for one already-resolved, specific phone identifier —
  // shared by both the direct single-number path and the picker's selection.
  const dialResolvedNumber = useCallback((contactId: string | null, phoneNumber: string) => {
    const knownChannel = conversation?.channel;
    startPhoneCall(phoneNumber, contactId || undefined, {
      name: contact?.name || conversation?.contactName || 'Unknown Contact',
      avatar: contact?.profilePictureUrl || conversation?.profilePictureUrl,
      linkedChannels: knownChannel ? [knownChannel] : undefined,
    });
  }, [startPhoneCall, contact, conversation]);

  // A specific number was chosen from the phone picker — dial it via its
  // original identifiers.phone[] index (see phonePickerNumbers below for why
  // the index must be the RAW array position, not the deduplicated list's).
  const handlePhoneIdentifierPick = useCallback(async (phoneIndex: number) => {
    setPhonePickerNumbers(null);
    if (!phonePickerContactId) return;
    try {
      const res = await apiService.getContactDialTarget(phonePickerContactId, undefined, undefined, undefined, phoneIndex);
      const phoneNumber = res.data?.phoneNumber;
      if (!phoneNumber) return;
      dialResolvedNumber(phonePickerContactId, phoneNumber);
    } catch (error) {
      console.error('[ConversationScreen] Failed to resolve dial target for phoneIndex', phoneIndex, error);
    }
  }, [phonePickerContactId, dialResolvedNumber]);

  const handleCallContact = useCallback(async () => {
    setShowGroupActionsMenu(false);

    // Same readiness guard as ContactsList's call button — isPhoneDialerReady
    // only means the InnoCalls SDK object is mounted, not that its SIP
    // registration has actually completed, but it blocks the common case of
    // tapping Call the instant the screen renders, before the dialer exists.
    if (!isPhoneDialerReady) {
      console.warn('[ConversationScreen] Call button pressed before phone dialer is ready — ignoring press');
      showNotification('warning', t('common.error'), t('conversation.noPhoneNumber'));
      return;
    }

    const contactId = await resolveParentContactId();

    // Before resolving a default dial target, check whether the parent
    // contact has more than one saved phone number — if so, defer to the
    // picker instead of silently dialing whatever the server's default
    // priority resolves to (see ContactsList's identical call-icon picker).
    if (contactId) {
      try {
        const contactRes = await apiService.getParentContactById(contactId);
        const rawPhones: string[] = contactRes.data?.identifiers?.phone || [];
        const seen = new Set<string>();
        const uniquePhones: Array<{ value: string; index: number }> = [];
        rawPhones.forEach((phone, index) => {
          const normalized = phone.replace(/^\+/, '').trim();
          if (!seen.has(normalized)) {
            seen.add(normalized);
            uniquePhones.push({ value: phone, index });
          }
        });
        if (uniquePhones.length > 1) {
          setPhonePickerContactId(contactId);
          setPhonePickerNumbers(uniquePhones);
          return;
        }
      } catch (error) {
        console.error('[ConversationScreen] Failed to load parent contact for phone picker', contactId, error);
      }
    }

    // Resolve the REAL, unmasked number via the dedicated dial-target endpoint
    // rather than getPhoneNumber() — contact.phoneNumber/conversation.phoneNumber
    // may be display-masked (contacts.viewMobileNumber) for this user, and the
    // WebRTC dialer can't place a call with a masked string like "+*******4567".
    // Falls back to getPhoneNumber() only if we have no parent contact id to
    // resolve against (e.g. a conversation with no linked omni-contact yet).
    let phoneNumber: string | undefined;
    if (contactId) {
      try {
        const res = await apiService.getContactDialTarget(contactId);
        phoneNumber = res.data?.phoneNumber;
      } catch (error) {
        console.error('[ConversationScreen] Failed to resolve dial target for contact', contactId, error);
      }
    }
    if (!phoneNumber) {
      phoneNumber = getPhoneNumber();
    }
    if (!phoneNumber) {
      showNotification('warning', t('common.error'), t('conversation.noPhoneNumber'));
      return;
    }

    dialResolvedNumber(contactId, phoneNumber);
  }, [showNotification, t, resolveParentContactId, isPhoneDialerReady, dialResolvedNumber]);

  const handleOpenAssignTo = useCallback(() => {
    setShowGroupActionsMenu(false);
    if (canManageContactAssignments) {
      setShowAssignmentModal(true);
    } else {
      openAssignmentMenu();
    }
  }, [canManageContactAssignments, openAssignmentMenu]);

  const loadContactNotes = useCallback(async () => {
    setLoadingNotes(true);
    try {
      const parentContactId = await resolveParentContactId();
      if (!parentContactId) {
        setContactNotes([]);
        return;
      }
      const result = await apiService.getParentContactById(parentContactId);
      if (result.statusCode === 1000 && result.data) {
        setContactNotes(result.data.notes || []);
      }
    } catch (error) {
      console.error('Failed to load contact notes:', error);
    } finally {
      setLoadingNotes(false);
    }
  }, [resolveParentContactId]);

  const notesSeenStorageKey = useCallback((parentContactId: string) => `conversation.notesSeenCount.${parentContactId}`, []);

  // Runs whenever the conversation's contact resolves — compares the contact's
  // current note count against the last count this device marked as seen, so the
  // header can show an indicator without the user having to open the Notes modal.
  const checkUnseenNotes = useCallback(async () => {
    const parentContactId = await resolveParentContactId();
    if (!parentContactId) {
      setHasUnseenNotes(false);
      return;
    }
    try {
      const result = await apiService.getParentContactById(parentContactId);
      const notes = result.statusCode === 1000 ? (result.data?.notes || []) : [];
      const seenRaw = await AsyncStorage.getItem(notesSeenStorageKey(parentContactId));
      const seenCount = seenRaw ? parseInt(seenRaw, 10) : 0;
      setHasUnseenNotes(notes.length > seenCount);
    } catch (error) {
      console.error('Failed to check for new notes:', error);
    }
  }, [resolveParentContactId, notesSeenStorageKey]);

  const handleOpenNotes = useCallback(async () => {
    setShowGroupActionsMenu(false);
    setShowNotesModal(true);
    setTimeout(() => notesInputRef.current?.focus(), 300);
    await loadContactNotes();
    setHasUnseenNotes(false);
    const parentContactId = await resolveParentContactId();
    if (parentContactId) {
      try {
        const result = await apiService.getParentContactById(parentContactId);
        const count = result.statusCode === 1000 ? (result.data?.notes?.length || 0) : 0;
        await AsyncStorage.setItem(notesSeenStorageKey(parentContactId), String(count));
      } catch (error) {
        console.error('Failed to mark notes as seen:', error);
      }
    }
  }, [loadContactNotes, resolveParentContactId, notesSeenStorageKey]);

  const handleAddNote = useCallback(async (content: string) => {
    if (!canManageNotes) return;
    const parentContactId = await resolveParentContactId();
    if (!parentContactId) {
      showNotification('error', t('common.error'), t('conversation.noContactToView'));
      return;
    }
    try {
      const result = await apiService.addContactNote(parentContactId, content);
      if (result.statusCode === 1000 && result.data) {
        const notes = result.data.notes || [];
        setContactNotes(notes);
        await AsyncStorage.setItem(notesSeenStorageKey(parentContactId), String(notes.length));
      } else {
        showNotification('error', t('common.error'), t('conversation.addNoteFailed'));
      }
    } catch (error: any) {
      showNotification('error', t('common.error'), t('conversation.addNoteFailed'));
    }
  }, [canManageNotes, resolveParentContactId, notesSeenStorageKey, showNotification, t]);

  const handleDeleteNote = useCallback(async (noteId: string | number) => {
    if (!canManageNotes) return;
    const parentContactId = await resolveParentContactId();
    if (!parentContactId) return;
    try {
      const result = await apiService.deleteContactNote(parentContactId, String(noteId));
      if (result.statusCode === 1000 && result.data) {
        const notes = result.data.notes || [];
        setContactNotes(notes);
        await AsyncStorage.setItem(notesSeenStorageKey(parentContactId), String(notes.length));
      } else {
        showNotification('error', t('common.error'), t('conversation.deleteNoteFailed'));
      }
    } catch (error: any) {
      showNotification('error', t('common.error'), t('conversation.deleteNoteFailed'));
    }
  }, [canManageNotes, resolveParentContactId, notesSeenStorageKey, showNotification, t]);

  useEffect(() => {
    const targetId = conversation?.contactId || contact?.id;
    if (targetId) {
      loadAssignments(targetId);
    }
  }, [conversation?.contactId, contact?.id, loadAssignments]);

  useEffect(() => {
    const targetId = conversation?.contactId || contact?.id;
    if (targetId) {
      checkUnseenNotes();
    }
  }, [conversation?.contactId, contact?.id, checkUnseenNotes]);

  // Load conversation details: prefer getConversation(conversationId) so we get contactLastMessageAt/messageCount for 24h window
  const loadConversation = async () => {
    try {
      console.log('🔍 [ConversationScreen] Loading conversation details for:', conversationId);

      const convResult = await apiService.getConversation(conversationId);
      if (convResult.statusCode === 1000 && convResult.data) {
        const c = convResult.data;
        const conversation: Conversation = {
          _id: c.id || conversationId,
          conversationId: c.id || conversationId,
          contactId: c.contactId,
          parentContactId: (c as any).parentContactId,
          phoneNumber: c.phoneNumber ?? '',
          contactName: c.contactName ?? 'Unknown Contact',
          channel: c.channel ?? 'whatsapp',
          status: c.status ?? 'active',
          priority: c.priority ?? 'normal',
          profilePictureUrl: c.profilePictureUrl,
          participants: [{ phoneNumber: c.phoneNumber ?? '', name: c.contactName ?? 'Unknown', isBusiness: false }],
          lastMessageAt: c.lastMessageAt ?? new Date().toISOString(),
          contactLastMessageAt: c.contactLastMessageAt,
          messageCount: c.messageCount ?? 0,
          unreadCount: c.unreadCount ?? 0,
          engagementCooldown: (c as any).engagementCooldown
        };
        const contact: Contact = {
          id: c.contactId,
          parentContactId: (c as any).parentContactId,
          name: c.contactName ?? 'Unknown',
          phoneNumber: c.phoneNumber ?? '',
          profilePicture: c.profilePictureUrl,
          isOnline: false,
          isBlocked: false,
          tags: c.tags ?? []
        };
        setConversation(conversation);
        setContact(contact);
        return;
      }

      // Fallback: get from unified list (e.g. if getConversation not available or failed)
      const conversationsResult = await apiService.getUnifiedConversations({ limit: 100, offset: 0 });
      if (conversationsResult.statusCode === 1000 && conversationsResult.data) {
        const conversations = conversationsResult.data.conversations || [];
        const currentConversation = conversations.find((conv: any) => conv.id === conversationId);
        if (currentConversation) {
          const conversation: Conversation = {
            _id: currentConversation.id,
            conversationId: currentConversation.id,
            contactId: currentConversation.contactId,
            parentContactId: (currentConversation as any).parentContactId,
            phoneNumber: currentConversation.phoneNumber ?? '',
            contactName: currentConversation.contactName,
            channel: currentConversation.channel,
            status: currentConversation.status,
            priority: currentConversation.priority,
            profilePictureUrl: currentConversation.profilePictureUrl,
            participants: [{ phoneNumber: currentConversation.phoneNumber ?? '', name: currentConversation.contactName, isBusiness: false }],
            lastMessageAt: currentConversation.lastMessageAt,
            contactLastMessageAt: currentConversation.contactLastMessageAt,
            messageCount: currentConversation.messageCount ?? 0,
            unreadCount: currentConversation.unreadCount ?? 0
          };
          setConversation(conversation);
          setContact({
            id: currentConversation.contactId,
            parentContactId: (currentConversation as any).parentContactId,
            name: currentConversation.contactName,
            phoneNumber: currentConversation.phoneNumber ?? '',
            profilePicture: currentConversation.profilePictureUrl,
            isOnline: false,
            isBlocked: false,
            tags: currentConversation.tags ?? []
          });
          return;
        }
      }

      setConversation({
        _id: conversationId,
        conversationId,
        contactId: 'unknown',
        phoneNumber: '',
        contactName: 'Unknown Contact',
        channel: 'whatsapp',
        status: 'active',
        priority: 'normal',
        participants: [],
        lastMessageAt: new Date().toISOString(),
        messageCount: 0,
        unreadCount: 0
      });
    } catch (error) {
      console.error('Error loading conversation:', error);
      showNotification('error', t('common.error'), t('conversation.loadFailed'));
    }
  };

  // Load messages - now uses omni-channel API with pagination
  const loadMessages = async (page: number = 0, isLoadMore: boolean = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setCurrentPage(0);
        setHasMoreMessages(true);
        shouldScrollToBottomRef.current = true;
        hasScrolledToEndOnLoadRef.current = false;
      }
      
      console.log('🔍 [ConversationScreen] Loading messages for conversation:', conversationId, 'page:', page);

      const result = await apiService.getConversationMessages(conversationId, {
        limit: 25,
        offset: page * 25,
        sortBy: 'timestamp',
        sortOrder: 'desc' // Always get most recent first, then use offset to get older messages
      });

      if (result.statusCode === 1000 && result.data) {
        const messages = result.data.messages || [];
        console.log('🔍 [ConversationScreen] Loaded messages:', messages.length);
        
        // Transform messages to match our Message interface
        
        const transformedMessages = messages.map((msg: any) => {
          // Debug reply data
          if (msg.replyToMessage) {
            console.log('🔍 [ConversationScreen] Reply message received:', {
              messageId: msg.messageId,
              replyTo: msg.replyTo,
              replyToMessage: msg.replyToMessage,
              content: msg.content
            });
          }
          
          const replyToMessageSource = msg.replyToMessage && typeof msg.replyToMessage === 'object'
            ? msg.replyToMessage
            : undefined;

          const replyToTransformed = replyToMessageSource ? {
            ...replyToMessageSource,
            _id: replyToMessageSource.id || replyToMessageSource._id || replyToMessageSource.messageId,
            timestamp: replyToMessageSource.timestamp || replyToMessageSource.createdAt || replyToMessageSource.updatedAt
          } : undefined;
          
          return {
            _id: msg.id || msg._id || msg.messageId,
            messageId: msg.messageId,
            contactId: msg.contactId,
            from: msg.from,
            to: msg.to,
            type: msg.type,
            content: msg.content,
            mediaUrl: msg.mediaUrl,
            mediaType: msg.mediaType,
            timestamp: msg.timestamp,
            direction: msg.direction,
            status: msg.status || 'delivered',
            errorMessage: msg.errorMessage,
            errorCode: msg.errorCode,
            providerId: msg.providerId,
            phoneNumberId: msg.phoneNumberId,
            tags: msg.tags || [],
            category: msg.category,
            priority: msg.priority,
            createdAt: msg.createdAt,
            updatedAt: msg.updatedAt,
            // Reply message support
            ...(msg.replyTo && { replyTo: msg.replyTo }),
            replyToMessage: replyToTransformed,
            externalId: msg.externalId, // Include WhatsApp message ID for reply linking
            // Contact information for reply indicators
            contactName: msg.contactName,
            contactPhone: msg.contactPhone,
            interactive: msg.interactive,
            interactiveType: msg.interactiveType,
            // CTA URL (product/catalog) and other interactive payloads from API
            ...(msg.interactiveData && { interactiveData: msg.interactiveData }),
            // Order/product/catalog reference for special cards
            ...(msg.referenceType && { referenceType: msg.referenceType }),
            ...(msg.referenceId && { referenceId: msg.referenceId }),
            ...(msg.externalReferences && { externalReferences: msg.externalReferences }),
          };
        });

        if (isLoadMore) {
          // Append older messages to the end (list is inverted)
          setMessages(prevMessages => [...prevMessages, ...transformedMessages]);
          setCurrentPage(page);
        } else {
          // Initial load: keep newest-first order from API (list is inverted)
          setMessages(transformedMessages);
          setCurrentPage(0);
        }
        
        // Check if we have more messages to load
        setHasMoreMessages(transformedMessages.length === 25);
        
        // Update conversation details from first message if available
        if (transformedMessages.length > 0) {
          const firstMsg = transformedMessages[0];
          setConversation(prev => prev ? {
            ...prev,
            contactId: firstMsg.contactId,
            phoneNumber: firstMsg.from,
            participants: [{ 
              phoneNumber: firstMsg.from, 
              name: firstMsg.from, // Will be enhanced later
              isBusiness: false 
            }],
            messageCount: transformedMessages.length,
            lastMessageAt: firstMsg.timestamp
          } : null);
        }
        
        // Mark messages as read
        markMessagesAsRead();
      } else {
        console.log('⚠️ [ConversationScreen] No messages found or error:', result.errors);
        showNotification('error', t('common.error'), typeof result.errors?.[0] === 'string' ? result.errors[0] : t('conversation.messageLoadFailed'));
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      showNotification('error', t('common.error'), t('conversation.messageLoadFailed'));
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Load more messages when scrolling to top
  const loadMoreMessages = async () => {
    const now = Date.now();
    // Prevent multiple calls within 2 seconds
    if (!loadingMore && hasMoreMessages && (now - lastLoadTrigger) > 2000) {
      setLastLoadTrigger(now);
      const nextPage = currentPage + 1;
      await loadMessages(nextPage, true);
    }
  };

  // Mark messages as read - enhanced for omni-channel
  const markMessagesAsRead = async () => {
    try {
      if (!conversation) {
        console.log('⚠️ [ConversationScreen] No conversation available for marking as read');
        return;
      }
      
      console.log('🔍 [ConversationScreen] Marking messages as read for conversation:', {
        conversationId,
        contactId: conversation.contactId,
        unreadCount: conversation.unreadCount,
        channel: conversation.channel
      });
      
      // Always update the chat store first to provide immediate UI feedback
      console.log('🔍 [ConversationScreen] Updating chat store immediately for conversation:', conversationId);
      markAsReadInStore(conversationId);
      
      // Emit socket event to notify other screens about the read status change
      socketService.emit('conversationMarkedAsRead', { conversationId });
      
      // Update local conversation state immediately
      if (conversation.unreadCount > 0) {
        console.log('🔍 [ConversationScreen] Resetting local unread count from', conversation.unreadCount, 'to 0');
        setConversation(prev => prev ? { ...prev, unreadCount: 0 } : null);
      }
      
      // Update local message status immediately
      console.log('🔍 [ConversationScreen] Updating local message status to read');
      setMessages(prev => {
        const updatedMessages = prev.map(msg => ({
          ...msg,
          status: msg.direction === 'inbound' ? 'read' : msg.status
        }));
        console.log('🔍 [ConversationScreen] Updated messages:', updatedMessages.map(m => ({ id: m._id, direction: m.direction, status: m.status })));
        return updatedMessages;
      });
      
      // Then call the API to update the backend
      console.log('🔍 [ConversationScreen] Calling API to mark messages as read in backend');
      const result = await apiService.markConversationAsRead(conversationId, conversation.contactId);
      console.log('🔍 [ConversationScreen] Mark as read API result:', result);
      
      if (result.statusCode !== 1000) {
        console.log('❌ [ConversationScreen] Failed to mark conversation as read in backend:', result.errors);
        // Even if API fails, we've already updated the local state for better UX
      } else {
        console.log('✅ [ConversationScreen] Successfully marked conversation as read in backend');
      }
      
      console.log('✅ [ConversationScreen] Messages marked as read successfully');
    } catch (error) {
      console.error('❌ [ConversationScreen] Error marking messages as read:', error);
      // Even if there's an error, the local state has been updated for better UX
    }
  };

  // Handle reply to message
  const handleReply = (message: Message) => {
    setReplyingTo(message);
  };

  // Cancel reply
  const cancelReply = () => {
    setReplyingTo(null);
  };

  // Context menu handlers
  const handleForward = (message: Message) => {
    setMessageToForward(message);
    setShowForwardModal(true);
    // Note: Forward functionality is fully implemented via ForwardMessageModal
  };

  const handleForwardConfirm = useCallback(
    async (targets: ForwardTargetSelection[]) => {
      if (!messageToForward) {
        setShowForwardModal(false);
        return;
      }

      if (!targets || targets.length === 0) {
        setShowForwardModal(false);
        setMessageToForward(null);
        return;
      }

      setIsForwarding(true);
      try {
        const payload = {
          source: {
            channel: 'whatsapp',
            messageId: messageToForward._id,
            messageInternalId: messageToForward.messageId,
            internalMessageId: messageToForward.messageId,
            externalId: messageToForward.externalId,
          },
          targets: targets.map((target) => ({
            channel: target.channel,
            conversationId: target.conversationId,
            contactId: target.contactId,
            contactName: target.contactName,
            phoneNumber: target.phoneNumber,
            providerId: target.providerId,
            phoneNumberId: target.phoneNumberId,
          })),
        };

        const response = await apiService.forwardMessage(payload);

        if (response.statusCode === 1000) {
          const successMessage =
            response.data?.message || 'Forwarding task created successfully.';
          showNotification('success', t('conversation.forwardMessage'), successMessage);
          setShowForwardModal(false);
          setMessageToForward(null);
        } else {
          showNotification(
            'error',
            'Forward Message',
            response.errors?.[0]?.message || 'Failed to forward message'
          );
        }
      } catch (error: any) {
        console.error('❌ [ConversationScreen] Error forwarding message:', error);
        showNotification(
          'error',
          'Forward Message',
          error?.message || 'Unexpected error while forwarding message'
        );
      } finally {
        setIsForwarding(false);
      }
    },
    [messageToForward, showNotification]
  );

  const handleCopy = (message: Message) => {
    showNotification('success', t('conversation.copied'), t('conversation.copiedDesc'));
  };

  const handleShareMessage = (message: Message) => {
    showNotification('success', t('conversation.shared'), t('conversation.sharedDesc'));
  };

  const handleDelete = (message: Message) => {
    // TODO: Implement delete message functionality
    showNotification('info', 'Delete', 'Delete functionality is being configured. Please contact support for assistance.');
  };

  const handleStar = (message: Message) => {
    const messageId = message._id;
    setStarredMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
        showNotification('info', 'Unstarred', 'Message unstarred');
      } else {
        newSet.add(messageId);
        showNotification('success', t('conversation.starred'), t('conversation.starredDesc'));
      }
      return newSet;
    });
  };

  const handleInfo = (message: Message) => {
    showNotification('info', 'Message Info', `Message ID: ${message._id}\nType: ${message.type}\nStatus: ${message.status}`);
  };

  const findMessageIndex = useCallback((messageId: string) => {
    const list = messagesRef.current;
    let index = list.findIndex(msg => {
      const idMatch = msg._id === messageId;
      const messageIdMatch = msg.messageId === messageId;
      const externalIdMatch = msg.externalId === messageId;
      return idMatch || messageIdMatch || externalIdMatch;
    });

    if (index !== -1) {
      return index;
    }

    const candidate = list.findIndex(msg => {
      const ids = [msg._id, msg.messageId, msg.externalId].filter(Boolean);
      return ids.some(idValue => {
        if (!idValue || typeof idValue !== 'string' || !messageId) {
          return false;
        }
        return messageId.includes(idValue) || idValue.includes(messageId);
      });
    });

    return candidate;
  }, []);

  const getStableMessageId = useCallback((message?: Message | null) => {
    if (!message) {
      return null;
    }
    return message.messageId || message._id || message.externalId || null;
  }, []);

  const scheduleMessageHighlight = useCallback((message?: Message) => {
    const targetId = getStableMessageId(message);
    if (!targetId) {
      return;
    }

    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = null;
    }

    const applyHighlight = () => {
      setHighlightedMessageId(targetId);
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedMessageId(null);
        highlightTimeoutRef.current = null;
      }, 2400);
    };

    if (highlightedMessageIdRef.current === targetId) {
      setHighlightedMessageId(null);
      requestAnimationFrame(applyHighlight);
    } else {
      applyHighlight();
    }
  }, [getStableMessageId]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    };
  }, []);

  const scrollToMessageIndex = useCallback((index: number) => {
    const list = messagesRef.current;
    if (index < 0 || index >= list.length) {
      return false;
    }

    const targetMessage = list[index];
    try {
      flatListRef.current?.scrollToIndex({
        index,
        animated: true,
        viewPosition: 0.5,
      });
      scheduleMessageHighlight(targetMessage);
      return true;
    } catch (scrollToIndexError) {
      try {
        const itemHeight = 100;
        const offset = index * itemHeight;
        flatListRef.current?.scrollToOffset({ offset, animated: true });
        scheduleMessageHighlight(targetMessage);
        return true;
      } catch (scrollToOffsetError) {
        console.error('❌ [ConversationScreen] Failed to scroll to message:', scrollToOffsetError);
        showNotification('error', 'Scroll Failed', `Could not scroll to message: ${(scrollToOffsetError as Error).message}`);
        return false;
      }
    }
  }, [scheduleMessageHighlight, showNotification]);

  const handleScrollToMessage = useCallback(async (messageId: string) => {
    if (!messageId) {
      return;
    }

    console.log('🔍 [ConversationScreen] Attempting to scroll to message:', messageId);

    const attemptImmediateScroll = () => {
      const index = findMessageIndex(messageId);
      if (index !== -1) {
        console.log('🔍 [ConversationScreen] Found message at index:', index);
        scrollToMessageIndex(index);
        pendingScrollTargetRef.current = null;
        scrollLoadAttemptsRef.current = 0;
        return true;
      }
      return false;
    };

    if (attemptImmediateScroll()) {
      return;
    }

    if (!hasMoreMessagesRef.current) {
      showNotification('error', 'Message Not Found', `Could not find message with ID: ${messageId}.`);
      pendingScrollTargetRef.current = null;
      scrollLoadAttemptsRef.current = 0;
      return;
    }

    pendingScrollTargetRef.current = messageId;
    scrollLoadAttemptsRef.current = 0;

    if (!isFetchingForScrollRef.current) {
      isFetchingForScrollRef.current = true;
      try {
        await loadMessages(currentPageRef.current + 1, true);
      } catch (error) {
        console.error('❌ [ConversationScreen] Failed to load additional messages for scroll:', error);
      } finally {
        isFetchingForScrollRef.current = false;
      }
    }
  }, [findMessageIndex, loadMessages, scrollToMessageIndex, showNotification]);

  useEffect(() => {
    const targetId = pendingScrollTargetRef.current;
    if (!targetId) {
      return;
    }

    const attemptScroll = () => {
      const index = findMessageIndex(targetId);
      if (index !== -1) {
        console.log('🔍 [ConversationScreen] Deferred scroll found target at index:', index);
        scrollToMessageIndex(index);
        pendingScrollTargetRef.current = null;
        scrollLoadAttemptsRef.current = 0;
        return true;
      }
      return false;
    };

    if (attemptScroll()) {
      return;
    }

    if (!hasMoreMessagesRef.current || scrollLoadAttemptsRef.current >= 10) {
      showNotification('error', 'Message Not Found', `Could not find message with ID: ${targetId}.`);
      pendingScrollTargetRef.current = null;
      scrollLoadAttemptsRef.current = 0;
      return;
    }

    if (isFetchingForScrollRef.current) {
      return;
    }

    isFetchingForScrollRef.current = true;
    scrollLoadAttemptsRef.current += 1;
    const nextPage = currentPageRef.current + 1;
    loadMessages(nextPage, true)
      .catch((error) => {
        console.error('❌ [ConversationScreen] Failed to load messages during deferred scroll:', error);
      })
      .finally(() => {
        isFetchingForScrollRef.current = false;
      });
  }, [messages, findMessageIndex, loadMessages, scrollToMessageIndex, showNotification]);

  // Send message - now uses omni-channel API
  const sendMessage = async (
    content: string,
    type: 'text' | 'image' | 'video' | 'audio' | 'document' | 'location' | 'location_request' | 'interactive' = 'text',
    mediaUri?: string,
    mediaDuration?: number,
    locationData?: {
      latitude: number;
      longitude: number;
      address: string;
    },
    mediaMetadata?: {
      fileName?: string;
      fileSize?: number;
      mimeType?: string;
    },
    messageOptions?: {
      aiChoiceDraft?: boolean;
      referenceType?: 'product' | 'catalog' | 'calendar' | 'order';
      referenceId?: string;
      interactiveType?: string;
      interactive?: any;
      selectedCatalog?: {
        _id: string;
        name: string;
      };
    }
  ) => {
    const hasCatalogAttachment = !!(messageOptions?.referenceType === 'catalog' && messageOptions?.referenceId);
    if (!conversation || (!content.trim() && !['audio', 'image', 'video', 'document', 'location', 'location_request', 'interactive'].includes(type) && !hasCatalogAttachment)) return;

    const tempId = `temp_${Date.now()}`;
    const tempTimestamp = new Date().toISOString();

    const shouldBlockInput = type === 'text' || messageOptions?.aiChoiceDraft === true;

    try {
      if (shouldBlockInput) {
        setSending(true);
      }
      console.log('🔍 [ConversationScreen] Sending message:', {
        content,
        type,
        locationData,
        hasLocationData: !!locationData
      });

      const derivedType: Message['type'] = hasCatalogAttachment
        ? 'interactive'
        : (type === 'location_request' ? 'location' : type as Message['type']);

      const optimisticMessage: Message = {
        _id: tempId,
        messageId: tempId,
        contactId: conversation.contactId,
        from: 'business',
        to: conversation.phoneNumber,
        type: derivedType,
        content: type === 'location'
          ? (locationData?.address || content.trim())
          : (content.trim() || ''),
        ...(type === 'location' && locationData && {
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          address: locationData.address,
        }),
        timestamp: tempTimestamp,
        direction: 'outbound-api',
        status: 'pending',
        providerId: 'omni-channel',
        phoneNumberId: conversation.phoneNumber,
        tags: [],
        createdAt: tempTimestamp,
        updatedAt: tempTimestamp,
        ...(replyingTo && {
          replyTo: replyingTo.messageId || replyingTo._id || replyingTo.externalId,
          replyToMessage: replyingTo,
          interactiveType: 'reply',
          ...(replyingTo.interactive && { interactive: replyingTo.interactive })
        }),
        ...(messageOptions?.referenceType && { referenceType: messageOptions.referenceType }),
        ...(messageOptions?.referenceId && { referenceId: messageOptions.referenceId }),
        ...(messageOptions?.interactiveType && { interactiveType: messageOptions.interactiveType }),
        ...(messageOptions?.interactive && { interactive: messageOptions.interactive }),
      };

      setMessages(prev => [optimisticMessage, ...prev]);
      setConversation(prev => prev ? { ...prev, lastMessageAt: tempTimestamp } : prev);
      setTimeout(() => scrollToBottom(true), 50);

      // Handle media file upload for all media types
      let mediaUrl = '';
      let mediaId = '';
      let mediaType = '';
      let filename = '';
      if ((type === 'audio' || type === 'image' || type === 'video' || type === 'document') && mediaUri) {
        console.log(`📎 Uploading ${type} file:`, { mediaUri, mediaDuration });
        
        // Basic file accessibility check
        if (!mediaUri || mediaUri.trim() === '') {
          showNotification('error', `${type.charAt(0).toUpperCase() + type.slice(1)} File Issue`, `The ${type} file may be corrupted or deleted. Please try again.`);
          if (shouldBlockInput) {
            setSending(false);
          }
          return;
        }
        
        try {
          console.log(`📎 Starting ${type} upload to backend...`);
          // Determine file extension and MIME type based on file URI and type
          const getMediaFileInfo = (uri: string, mediaType: string): { filename: string; mimeType: string } => {
            const timestamp = Date.now();
            
            // Extract original extension from URI
            const uriExtension = uri.split('.').pop()?.toLowerCase() || '';
            const isBlobUri = uri.startsWith('blob:');
            const mimeFromHash = (() => {
              if (!isBlobUri) return '';
              const hashIndex = uri.indexOf('#mime=');
              if (hashIndex === -1) return '';
              try {
                return decodeURIComponent(uri.substring(hashIndex + 6));
              } catch {
                return '';
              }
            })();
            
            switch (mediaType) {
              case 'audio':
                if (isBlobUri) {
                  const blobMime = mimeFromHash || 'audio/ogg';
                  if (blobMime.includes('webm')) {
                    return { filename: `voice_note_${timestamp}.webm`, mimeType: blobMime };
                  }
                  if (blobMime.includes('mp4') || blobMime.includes('m4a')) {
                    return { filename: `voice_note_${timestamp}.m4a`, mimeType: 'audio/mp4' };
                  }
                  if (blobMime.includes('ogg')) {
                    return { filename: `voice_note_${timestamp}.ogg`, mimeType: 'audio/ogg' };
                  }
                }

                if (uri.includes('.ogg')) {
                  return { filename: `voice_note_${timestamp}.ogg`, mimeType: 'audio/ogg' };
                } else if (uri.includes('.m4a') || Platform.OS === 'ios') {
                  return { filename: `voice_note_${timestamp}.m4a`, mimeType: 'audio/mp4' };
                } else if (uri.includes('.mp3')) {
                  return { filename: `audio_${timestamp}.mp3`, mimeType: 'audio/mpeg' };
                } else {
                  return { filename: `audio_${timestamp}.${uriExtension || 'aac'}`, mimeType: 'audio/aac' };
                }
              
              case 'image':
                if (uri.includes('.jpg') || uri.includes('.jpeg')) {
                  return { filename: `image_${timestamp}.jpg`, mimeType: 'image/jpeg' };
                } else if (uri.includes('.png')) {
                  return { filename: `image_${timestamp}.png`, mimeType: 'image/png' };
                } else if (uri.includes('.gif')) {
                  return { filename: `image_${timestamp}.gif`, mimeType: 'image/gif' };
                } else if (uri.includes('.webp')) {
                  return { filename: `image_${timestamp}.webp`, mimeType: 'image/webp' };
                } else {
                  return { filename: `image_${timestamp}.${uriExtension || 'jpg'}`, mimeType: 'image/jpeg' };
                }
              
              case 'video':
                if (uri.includes('.mp4')) {
                  return { filename: `video_${timestamp}.mp4`, mimeType: 'video/mp4' };
                } else if (uri.includes('.mov')) {
                  return { filename: `video_${timestamp}.mov`, mimeType: 'video/quicktime' };
                } else if (uri.includes('.avi')) {
                  return { filename: `video_${timestamp}.avi`, mimeType: 'video/x-msvideo' };
                } else {
                  return { filename: `video_${timestamp}.${uriExtension || 'mp4'}`, mimeType: 'video/mp4' };
                }
              
              case 'document':
                if (uri.includes('.pdf')) {
                  return { filename: `document_${timestamp}.pdf`, mimeType: 'application/pdf' };
                } else if (uri.includes('.doc') || uri.includes('.docx')) {
                  return { filename: `document_${timestamp}.docx`, mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' };
                } else if (uri.includes('.xls') || uri.includes('.xlsx')) {
                  return { filename: `document_${timestamp}.xlsx`, mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' };
                } else if (uri.includes('.txt')) {
                  return { filename: `document_${timestamp}.txt`, mimeType: 'text/plain' };
                } else {
                  return { filename: `document_${timestamp}.${uriExtension || 'bin'}`, mimeType: 'application/octet-stream' };
                }
              
              default:
                return { filename: `file_${timestamp}.${uriExtension || 'bin'}`, mimeType: 'application/octet-stream' };
            }
          };

          const fileInfo = getMediaFileInfo(mediaUri, type);
          filename = fileInfo.filename;
          mediaType = fileInfo.mimeType;

          // Determine provider based on conversation channel
          const providerId = conversation?.channel === 'facebook-messenger' 
            ? 'default-facebook-messenger' 
            : 'default-whatsapp';
            
          console.log('🔍 [ConversationScreen] Media upload details:', {
            conversationChannel: conversation?.channel,
            providerId,
            mediaUri,
            filename,
            mediaType
          });
            
          const uploadResult = await apiService.uploadMedia(
            mediaUri,
            filename,
            mediaType,
            providerId
          );
          
          console.log('🎤 Upload result:', uploadResult);
          
          if (uploadResult.statusCode === 1000 && uploadResult.data) {
            mediaUrl = uploadResult.data.mediaUrl;
            mediaId = uploadResult.data.mediaId;
            mediaType = uploadResult.data.mediaType || mediaType; // Use server MIME type or detected type
            console.log(`✅ ${type} file uploaded successfully:`, { mediaUrl, mediaId, mediaType });

            const mediaDetails = {
              mediaUrl,
              mediaType,
              mediaName: filename || `${type}_${Date.now()}.bin`,
            };

            setMessages(prev =>
              prev.map(msg =>
                msg._id === tempId ? { ...msg, ...mediaDetails } : msg
              )
            );
          } else {
            console.warn(`⚠️ ${type} upload failed, using local URI. Result:`, uploadResult);
            
            // Show user-friendly notification for upload failures
            if (type === 'audio') {
              showNotification('error', t('conversation.audioFileIssue'), t('conversation.audioFileIssueDesc'));
            } else if (type === 'image') {
              showNotification('error', t('conversation.imageFileIssue'), t('conversation.imageFileIssueDesc'));
            } else if (type === 'video') {
              showNotification('error', t('conversation.videoFileIssue'), t('conversation.videoFileIssueDesc'));
            } else if (type === 'document') {
              showNotification('error', t('conversation.documentFileIssue'), t('conversation.documentFileIssueDesc'));
            } else {
              showNotification('error', t('conversation.fileUploadError'), t('conversation.fileUploadErrorDesc'));
            }

            // Don't proceed with sending the message if media upload failed
            if (shouldBlockInput) {
              setSending(false);
            }
            return;
          }
        } catch (error) {
          console.error(`❌ ${type} upload error:`, error);
          console.error('❌ Error details:', JSON.stringify(error, null, 2));
          
          // Show user-friendly notification for media upload errors
          if (type === 'audio') {
            showNotification('error', t('conversation.audioFileIssue'), t('conversation.audioFileIssueDesc'));
          } else if (type === 'image') {
            showNotification('error', t('conversation.imageFileIssue'), t('conversation.imageFileIssueDesc'));
          } else if (type === 'video') {
            showNotification('error', t('conversation.videoFileIssue'), t('conversation.videoFileIssueDesc'));
          } else if (type === 'document') {
            showNotification('error', t('conversation.documentFileIssue'), t('conversation.documentFileIssueDesc'));
          } else {
            showNotification('error', t('conversation.fileUploadError'), t('conversation.fileUploadErrorDesc'));
          }
          
          // Don't proceed with sending the message if media upload failed
          setMessages(prev =>
            prev.map(msg =>
              msg._id === tempId
                ? { ...msg, status: 'failed', updatedAt: new Date().toISOString() }
                : msg
            )
          );
          if (shouldBlockInput) {
            setSending(false);
          }
          return;
        }
      }

      // Debug logging for media message conditions
      console.log('🔍 [ConversationScreen] Debug media conditions:', {
        type,
        mediaUri,
        mediaUrl,
        mediaId,
        hasMediaUri: !!mediaUri,
        hasMediaUrl: !!mediaUrl,
        hasMediaId: !!mediaId,
        willIncludeMedia: !!((type === 'audio' || type === 'image' || type === 'video' || type === 'document') && mediaUri && mediaUrl)
      });

      let messageData;
      try {
        console.log('📤 [ConversationScreen] Constructing messageData for type:', type);
        const trimmedContent = content.trim();
        console.log('📤 [ConversationScreen] Content received:', { type, content, trimmedContent, length: trimmedContent.length });
        messageData = {
          content: (type === 'location' || type === 'location_request')
            ? trimmedContent
            : trimmedContent,
          type: hasCatalogAttachment ? 'interactive' : derivedType,
          messagingType: 'RESPONSE' as const,
          // Include media information for all media types
          ...(((type === 'audio' || type === 'image' || type === 'video' || type === 'document') && mediaUri && mediaUrl) && {
            mediaUrl: mediaUrl, // Use the actual media URL from upload response
            mediaType: mediaType, // Use MIME type from upload response
            mediaName: filename || `${type}_${Date.now()}.bin`,
            mediaSize: 0, // Would be calculated from actual file size
          }),
          // Include location data for location messages
          ...(type === 'location' && locationData && {
            latitude: locationData.latitude,
            longitude: locationData.longitude,
            address: locationData.address,
          }),
          // Include reply data if replying
          ...(replyingTo && {
            replyTo: replyingTo.messageId || replyingTo._id || replyingTo.externalId,
            replyToMessage: {
              messageId: replyingTo.messageId || replyingTo._id,
              content: replyingTo.content,
              type: replyingTo.type,
              from: replyingTo.from
            }
          }),
          ...(messageOptions?.referenceType && { referenceType: messageOptions.referenceType }),
          ...(messageOptions?.referenceId && { referenceId: messageOptions.referenceId }),
          ...(hasCatalogAttachment && { interactiveType: 'catalog' }),
          ...((hasCatalogAttachment || messageOptions?.interactive) && {
            interactive: hasCatalogAttachment
              ? {
                  type: 'catalog',
                  selectedCatalog: messageOptions?.selectedCatalog || {
                    _id: messageOptions?.referenceId,
                    name: 'Catalog'
                  }
                }
              : messageOptions?.interactive
          }),
        };
        console.log('📤 [ConversationScreen] MessageData constructed successfully');
      } catch (error) {
        console.error('❌ [ConversationScreen] Error constructing messageData:', error);
        showNotification('error', t('conversation.messageError'), t('conversation.messageErrorDesc'));
        setSending(false);
        return;
      }

      console.log('📤 [ConversationScreen] Sending message data:', messageData);
      console.log('📤 [ConversationScreen] Text message debug - content:', content, 'type:', type, 'replyingTo:', replyingTo);
      console.log('📤 [ConversationScreen] ReplyingTo debug - exists:', !!replyingTo, 'externalId:', replyingTo?.externalId, 'messageId:', replyingTo?.messageId, '_id:', replyingTo?._id);
      console.log('📤 [ConversationScreen] Location data check:', {
        type,
        locationData,
        hasLocationData: !!locationData,
        messageDataLocation: messageData.latitude ? {
          latitude: messageData.latitude,
          longitude: messageData.longitude,
          address: messageData.address
        } : 'No location data in messageData'
      });
      console.log('📤 [ConversationScreen] About to call apiService.sendConversationMessage with conversationId:', conversationId);
      const result = await apiService.sendConversationMessage(conversationId, messageData);
      console.log('📤 [ConversationScreen] API call completed, result:', result);

      if (result.statusCode === 1000) {
        console.log('✅ [ConversationScreen] Message sent successfully');
        setSentVersion(v => v + 1);
        
        const finalTimestamp = result.data?.timestamp || new Date().toISOString();

        const sentId = result.data?.id || result.data?._id;
        setMessages(prev =>
          // If the socket already delivered this message, the placeholder is a duplicate.
          sentId && prev.some(msg => msg._id === sentId && msg._id !== tempId) ? prev.filter(msg => msg._id !== tempId) :
          prev.map(msg =>
            msg._id === tempId
              ? {
                  ...msg,
                  _id: result.data?.id || result.data?._id || msg._id,
                  messageId: result.data?.messageId || msg.messageId,
                  status: (result.data?.status as Message['status']) || 'sent',
                  timestamp: finalTimestamp,
                  updatedAt: finalTimestamp,
                  ...(result.data?.type && { type: result.data.type as Message['type'] }),
                  ...(result.data?.mediaUrl && { mediaUrl: result.data.mediaUrl }),
                  ...(result.data?.mediaType && { mediaType: result.data.mediaType }),
                  ...(result.data?.content && { content: result.data.content }),
                  ...(result.data?.interactive && { interactive: result.data.interactive }),
                  ...(result.data?.replyTo && { replyTo: result.data.replyTo }),
                  ...(result.data?.replyToMessage && { replyToMessage: result.data.replyToMessage }),
                  ...(result.data?.interactiveType && { interactiveType: result.data.interactiveType }),
                }
              : msg
          )
        );

        setConversation(prev =>
          prev
            ? {
                ...prev,
                lastMessageAt: finalTimestamp,
              }
            : prev
        );

        setReplyingTo(null);
        if (messageOptions?.aiChoiceDraft) { setPendingAiChoices([]); setComposerText(''); }

        setTimeout(() => {
          scrollToBottom(true);
        }, 50);
      } else {
        console.log('❌ [ConversationScreen] Failed to send message:', result.errors);

        setMessages(prev =>
          prev.map(msg =>
            msg._id === tempId
              ? { ...msg, status: 'failed', updatedAt: new Date().toISOString() }
              : msg
          )
        );
        showNotification('error', t('conversation.sendFailed'), typeof result.errors?.[0] === 'string' ? result.errors[0] : t('conversation.sendFailed'));
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = (error as Error)?.message || '';
      const isTimeout = errorMessage.toLowerCase().includes('timeout');

      if (isTimeout) {
        // Keep message pending and wait for socket status updates
        setMessages(prev =>
          prev.map(msg =>
            msg._id === tempId
              ? { ...msg, status: 'pending', updatedAt: new Date().toISOString() }
              : msg
          )
        );
        showNotification('info', 'Sending…', 'Message is being sent. Status will update shortly.');
        return;
      }

      setMessages(prev =>
        prev.map(msg =>
          msg._id === tempId
            ? { ...msg, status: 'failed', updatedAt: new Date().toISOString() }
            : msg
        )
      );
      
      // Provide more specific error messages based on message type
      if (type === 'audio') {
        showNotification('error', t('conversation.audioFailed'), t('conversation.audioFailedDesc'));
      } else if (type === 'image') {
        showNotification('error', t('conversation.imageFailed'), t('conversation.imageFailedDesc'));
      } else if (type === 'video') {
        showNotification('error', t('conversation.videoFailed'), t('conversation.videoFailedDesc'));
      } else if (type === 'document') {
        showNotification('error', t('conversation.documentFailed'), t('conversation.documentFailedDesc'));
      } else {
        showNotification('error', t('conversation.messageFailed'), t('conversation.messageFailedDesc'));
      }
    } finally {
      if (shouldBlockInput) {
        setSending(false);
      }
    }
  };

  const handleRetryMessage = useCallback(async (message: Message) => {
    const isOutbound = message.direction === 'outbound' || message.direction === 'outbound-api';
    if (!isOutbound || message.status !== 'failed') {
      return;
    }

    const messageId = message._id;
    const retryStartedAt = new Date().toISOString();

    setMessages(prev =>
      prev.map(msg =>
        msg._id === messageId
          ? {
              ...msg,
              status: 'pending',
              errorMessage: undefined,
              errorCode: undefined,
              updatedAt: retryStartedAt,
            }
          : msg
      )
    );

    try {
      // A template is re-sent as the template it was (saved as its content), with its filled-in text.
      let retryTemplate: any;
      if (message.type === 'template') {
        try { const parsed = JSON.parse(message.content || ''); if (parsed?.name) retryTemplate = parsed; } catch { /* not a saved template */ }
        if (!retryTemplate) throw new Error('This template message cannot be retried — send it again from the template picker');
      }
      const messageData = {
        content: message.content || '',
        type: message.type,
        messagingType: 'RESPONSE' as const,
        ...(retryTemplate && {
          template: retryTemplate,
          ...((message as any).interactiveData && { interactiveType: 'template_resolved', interactiveData: (message as any).interactiveData }),
        }),
        ...(message.mediaUrl && { mediaUrl: message.mediaUrl }),
        ...(message.mediaType && { mediaType: message.mediaType }),
        ...(message.mediaName && { mediaName: message.mediaName }),
        ...(typeof message.mediaSize === 'number' && { mediaSize: message.mediaSize }),
        ...(typeof message.latitude === 'number' && typeof message.longitude === 'number' && {
          latitude: message.latitude,
          longitude: message.longitude,
          address: message.address,
        }),
        ...(message.replyTo && { replyTo: message.replyTo }),
        ...(message.replyToMessage && {
          replyToMessage: {
            messageId: message.replyToMessage.messageId || message.replyToMessage._id,
            content: message.replyToMessage.content,
            type: message.replyToMessage.type,
            from: message.replyToMessage.from,
          },
        }),
        ...(message.referenceType && { referenceType: message.referenceType }),
        ...(message.referenceId && { referenceId: message.referenceId }),
        ...(message.interactiveType && { interactiveType: message.interactiveType }),
        ...(message.interactive && { interactive: message.interactive }),
      };

      const result = await apiService.sendConversationMessage(conversationId, messageData);

      if (result.statusCode === 1000) {
        const finalTimestamp = result.data?.timestamp || new Date().toISOString();

        setMessages(prev =>
          prev.map(msg =>
            msg._id === messageId
              ? {
                  ...msg,
                  _id: result.data?.id || result.data?._id || msg._id,
                  messageId: result.data?.messageId || msg.messageId,
                  status: (result.data?.status as Message['status']) || 'sent',
                  timestamp: finalTimestamp,
                  updatedAt: finalTimestamp,
                  errorMessage: undefined,
                  errorCode: undefined,
                  ...(result.data?.type && { type: result.data.type as Message['type'] }),
                  ...(result.data?.mediaUrl && { mediaUrl: result.data.mediaUrl }),
                  ...(result.data?.mediaType && { mediaType: result.data.mediaType }),
                  ...(result.data?.content && { content: result.data.content }),
                  ...(result.data?.interactive && { interactive: result.data.interactive }),
                  ...(result.data?.replyTo && { replyTo: result.data.replyTo }),
                  ...(result.data?.replyToMessage && { replyToMessage: result.data.replyToMessage }),
                  ...(result.data?.interactiveType && { interactiveType: result.data.interactiveType }),
                }
              : msg
          )
        );
        setConversation(prev => prev ? { ...prev, lastMessageAt: finalTimestamp } : prev);
        setTimeout(() => scrollToBottom(true), 50);
        return;
      }

      const errorText = typeof result.errors?.[0] === 'string'
        ? result.errors[0]
        : t('conversation.sendFailed');
      setMessages(prev =>
        prev.map(msg =>
          msg._id === messageId
            ? {
                ...msg,
                status: 'failed',
                errorMessage: errorText,
                updatedAt: new Date().toISOString(),
              }
            : msg
        )
      );
      showNotification('error', t('conversation.sendFailed'), errorText);
    } catch (error) {
      const errorText = (error as Error)?.message || t('conversation.messageFailedDesc');
      setMessages(prev =>
        prev.map(msg =>
          msg._id === messageId
            ? {
                ...msg,
                status: 'failed',
                errorMessage: errorText,
                updatedAt: new Date().toISOString(),
              }
            : msg
        )
      );
      showNotification('error', t('conversation.messageFailed'), errorText);
    }
  }, [conversationId, scrollToBottom, showNotification, t]);

  const getCurrentParentContactId = useCallback(() => {
    return (
      contact?.parentContactId ||
      conversation?.parentContactId ||
      conversation?.contactId ||
      contact?.id ||
      null
    );
  }, [contact?.parentContactId, contact?.id, conversation?.parentContactId, conversation?.contactId]);

  const isCoordinateAddress = useCallback((value?: string) => {
    if (!value) return false;
    return /^\[?\s*(?:location\s*:\s*)?-?\d+\.?\d*\s*,\s*-?\d+\.?\d*\s*\]?$/i.test(value.trim());
  }, []);

  const isDetailedAddress = useCallback((address?: string, country?: string) => {
    const trimmed = address?.trim();
    if (!trimmed || isCoordinateAddress(trimmed)) return false;
    if (country && trimmed.toLowerCase() === country.trim().toLowerCase()) return false;
    return true;
  }, [isCoordinateAddress]);

  const reverseGeocodeMessageCoordinates = useCallback(async (latitude: number, longitude: number) => {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1&accept-language=en`
    );

    if (!response.ok) {
      throw new Error('Reverse geocoding failed');
    }

    const data = await response.json();
    const addr = data?.address || {};
    const city = addr.city || addr.town || addr.village || addr.county;
    const country = addr.country;
    const addressParts = [
      addr.road,
      addr.house_number,
      addr.neighbourhood || addr.suburb,
      city,
      addr.state || addr.region,
      country,
    ].filter(Boolean);
    const structuredAddress = addressParts.join(', ');
    const hasDetailedStructuredAddress = addressParts.length > 1 || !!(addr.road || city || addr.state || addr.region);
    const formattedAddress = (hasDetailedStructuredAddress ? structuredAddress : undefined) || data?.display_name || structuredAddress;

    return {
      latitude,
      longitude,
      address: formattedAddress,
      text: formattedAddress,
      ...(city && { city }),
      ...(country && { country }),
    };
  }, []);

  const resolveMessageLocationAddress = useCallback(async (location: MessageLocationPayload) => {
    const fallbackAddress = `${location.latitude}, ${location.longitude}`;
    const existingAddress = (location.address || location.text || '').trim();
    const hasUsefulAddress = isDetailedAddress(existingAddress, location.country);

    try {
      const mapsUrl = `https://maps.google.com/maps?q=${location.latitude},${location.longitude}`;
      const response = await apiService.resolveContactLocationUrl(mapsUrl);
      if (response.statusCode === 1000 && response.data) {
        const resolvedAddress =
          response.data.address ||
          response.data.text ||
          response.data.formatted ||
          (hasUsefulAddress ? existingAddress : fallbackAddress);

        if (isDetailedAddress(resolvedAddress, response.data.country || location.country)) {
          return {
            latitude: response.data.latitude ?? location.latitude,
            longitude: response.data.longitude ?? location.longitude,
            address: resolvedAddress,
            text: resolvedAddress,
            ...(response.data.city || location.city ? { city: response.data.city || location.city } : {}),
            ...(response.data.country || location.country ? { country: response.data.country || location.country } : {}),
          };
        }
      }
    } catch (error) {
      console.warn('[ConversationScreen] Failed to reverse-geocode message location via API:', error);
    }

    try {
      const geocoded = await reverseGeocodeMessageCoordinates(location.latitude, location.longitude);
      if (isDetailedAddress(geocoded.address, geocoded.country)) {
        return geocoded;
      }
    } catch (error) {
      console.warn('[ConversationScreen] Failed to reverse-geocode message location directly:', error);
    }

    if (hasUsefulAddress) {
      return {
        latitude: location.latitude,
        longitude: location.longitude,
        address: existingAddress,
        text: existingAddress,
        ...(location.city && { city: location.city }),
        ...(location.country && { country: location.country }),
      };
    }

    return {
      latitude: location.latitude,
      longitude: location.longitude,
      ...(location.city && { city: location.city }),
      ...(location.country && { country: location.country }),
    };
  }, [isDetailedAddress, reverseGeocodeMessageCoordinates]);

  const handleSetContactLocationFromMessage = useCallback(async (_message: Message, location: MessageLocationPayload) => {
    const contactId = getCurrentParentContactId();
    if (!contactId) {
      showNotification('error', 'Contact Location', 'Could not find the contact to update.');
      return;
    }

    const normalizedLocation = await resolveMessageLocationAddress(location);

    try {
      const response = await apiService.updateParentContact(contactId, {
        profile: {
          location: normalizedLocation,
        },
      });

      if (response.statusCode === 1000) {
        setContact(prev => prev ? { ...prev, location: normalizedLocation } : prev);
        bumpContactsRefreshToken();
        showNotification('success', 'Contact Location', 'Contact location updated.');
        return;
      }

      showNotification('error', 'Contact Location', response.errors?.[0]?.message || 'Failed to update contact location.');
    } catch (error: any) {
      showNotification('error', 'Contact Location', error?.message || 'Failed to update contact location.');
    }
  }, [bumpContactsRefreshToken, getCurrentParentContactId, resolveMessageLocationAddress, showNotification]);

  const handleCreateOrderAtMessageLocation = useCallback(async (_message: Message, location: MessageLocationPayload) => {
    const normalizedLocation = await resolveMessageLocationAddress(location);
    const contactId = getCurrentParentContactId();

    setActiveTab('orders');
    navigateTo('order-add', undefined, {
      prefillOrder: {
        source: 'conversation-location',
        conversationId,
        contactId,
        customer: {
          id: contactId,
          name: contact?.name || conversation?.contactName || '',
          phone: contact?.phoneNumber || conversation?.phoneNumber || '',
          location: normalizedLocation,
        },
        location: normalizedLocation,
      },
    });
  }, [contact?.name, contact?.phoneNumber, conversation?.contactName, conversation?.phoneNumber, conversationId, getCurrentParentContactId, navigateTo, resolveMessageLocationAddress, setActiveTab]);

  // Load conversation and messages when conversationId changes
  useEffect(() => {
    console.log('🔍 [ConversationScreen] Conversation ID changed, loading conversation and messages');
    hasScrolledToEndOnLoadRef.current = false;
    loadConversation();
    loadMessages();
  }, [conversationId]);

  // Additional effect to ensure conversation data is refreshed when component mounts
  useEffect(() => {
    console.log('🔍 [ConversationScreen] Component mounted, ensuring conversation data is fresh');
    // Small delay to ensure the component is fully mounted
    const timeoutId = setTimeout(() => {
      loadConversation();
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Auto-scroll to bottom only once when messages are first loaded (not on content size change or pagination)
  useEffect(() => {
    if (
      messages.length > 0 &&
      !loading &&
      !loadingMore &&
      !hasScrolledToEndOnLoadRef.current
    ) {
      hasScrolledToEndOnLoadRef.current = true;
      setTimeout(() => {
        scrollToBottom(false);
      }, 200);
    }
  }, [messages.length, loading, loadingMore]);

  // Mark messages as read when conversation is loaded and messages are loaded
  useEffect(() => {
    if (conversation && messages.length > 0 && !loading) {
      console.log('🔍 [ConversationScreen] Conversation and messages loaded, unreadCount:', conversation.unreadCount);
      console.log('🔍 [ConversationScreen] Conversation ID being used for mark as read:', conversationId);
      console.log('🔍 [ConversationScreen] Conversation object ID:', conversation.conversationId);
      console.log('🔍 [ConversationScreen] Conversation _id:', conversation._id);
      
      // Add a small delay to ensure everything is properly loaded
      const timeoutId = setTimeout(() => {
        if (conversation.unreadCount > 0) {
          console.log('🔍 [ConversationScreen] Conversation has unread messages, marking as read');
          markMessagesAsRead();
        } else {
          console.log('🔍 [ConversationScreen] No unread messages, but still marking as read to ensure consistency');
          markMessagesAsRead();
        }
      }, 500); // 500ms delay to ensure everything is loaded
      
      return () => clearTimeout(timeoutId);
    }
  }, [conversation, messages.length, loading]);

  // Setup real-time event listeners
  useEffect(() => {
    // Join the conversation room
    socketService.joinConversation(conversationId);

    // Listen for new messages in this conversation
    const handleNewMessage = (data: any) => {
      if (data.conversationId === conversationId) {
        console.log('🔌 [ConversationScreen] New message received:', data);
        
        const newMessage: Message = {
          _id: data.message._id || data.message.id || data.message.messageId,
          messageId: data.message.messageId,
          contactId: conversation?.contactId || data.message.contactId || '',
          from: data.message.from,
          to: data.message.to,
          type: data.message.type as Message['type'],
          content: data.message.content,
          mediaUrl: data.message.mediaUrl,
          mediaType: data.message.mediaType,
          timestamp: data.message.timestamp,
          direction: data.message.direction as Message['direction'],
          status: (data.message.status as Message['status']) || 'sent',
          providerId: data.message.providerId || (conversation?.channel === 'facebook-messenger'
            ? 'default-facebook-messenger'
            : 'default-whatsapp'),
          phoneNumberId: conversation?.phoneNumber || data.message.phoneNumberId || '',
          tags: data.message.tags || [],
          createdAt: data.message.createdAt || data.message.timestamp,
          updatedAt: data.message.updatedAt || data.message.timestamp,
          contactName: data.message.contactName || conversation?.contactName,
          contactPhone: data.message.contactPhone || conversation?.phoneNumber,
          replyTo: data.message.replyTo,
          replyToMessage: data.message.replyToMessage,
          externalId: data.message.externalId,
          interactive: data.message.interactive,
          interactiveType: data.message.interactiveType,
        };

        upsertMessage(newMessage);

        setTimeout(() => {
          scrollToBottom(true);
        }, 100);
      }
    };

    const handleMessageStatusUpdate = (data: any) => {
      if (data.conversationId !== conversationId) {
        return;
      }

      const updatedMessage: Message = {
        _id: data.message?._id || data.message?.id || data.messageId,
        messageId: data.messageId,
        externalId: data.externalId,
        contactId: conversation?.contactId || '',
        from: data.from || 'business',
        to: data.to || conversation?.phoneNumber || '',
        type: (data.message?.type || 'text') as Message['type'],
        content: data.message?.content || '',
        timestamp: data.timestamp || new Date().toISOString(),
        direction: (data.direction || 'outbound-api') as Message['direction'],
        status: (data.status as Message['status']) || 'sent',
        providerId: data.providerId || 'omni-channel',
        phoneNumberId: conversation?.phoneNumber || '',
        tags: data.tags || [],
        createdAt: data.createdAt || data.timestamp || new Date().toISOString(),
        updatedAt: data.updatedAt || new Date().toISOString(),
        interactive: data.message?.interactive,
        interactiveType: data.message?.interactiveType,
      };

      upsertMessage(updatedMessage);
    };

    const handleDeliveryStatus = (data: { messageId: string; status: Message['status']; timestamp?: string; errorMessage?: string; errorCode?: number }) => {
      if (!data?.messageId) {
        return;
      }
      setMessages(prev =>
        prev.map(msg =>
          msg.messageId === data.messageId
            ? {
                ...msg,
                status: (data.status as Message['status']) || msg.status,
                errorMessage: data.errorMessage ?? msg.errorMessage,
                errorCode: data.errorCode ?? msg.errorCode,
                updatedAt: data.timestamp || new Date().toISOString(),
              }
            : msg
        )
      );
    };

    socketService.onNewMessage(handleNewMessage);
    socketService.on('message-status-update', handleMessageStatusUpdate);
    socketService.on('message-updated', handleMessageStatusUpdate);
    socketService.on('whatsapp:delivery-status', handleDeliveryStatus);
    socketService.on('whatsapp:message-status', handleMessageStatusUpdate);

    // Cleanup: leave conversation room and remove listeners
    return () => {
      socketService.leaveConversation(conversationId);
      socketService.off('new-message', handleNewMessage);
      socketService.off('message-status-update', handleMessageStatusUpdate);
      socketService.off('message-updated', handleMessageStatusUpdate);
      socketService.off('whatsapp:delivery-status', handleDeliveryStatus);
      socketService.off('whatsapp:message-status', handleMessageStatusUpdate);
    };
  }, [conversationId, conversation, upsertMessage]);

  const getChannelIcon = (channel: string) => {
    switch (channel?.toLowerCase()) {
      case 'whatsapp': return 'logo-whatsapp';
      case 'telegram': return 'send';
      case 'facebook': return 'logo-facebook';
      case 'instagram': return 'logo-instagram';
      case 'twitter':
      case 'x': return 'logo-twitter';
      case 'email': return 'mail';
      default: return 'chatbubble';
    }
  };

  const getChannelColor = (channel: string) => {
    switch (channel?.toLowerCase()) {
      case 'whatsapp': return '#25D366';
      case 'telegram': return '#0088cc';
      case 'facebook': return '#1877f2';
      case 'instagram': return '#E4405F';
      case 'twitter':
      case 'x': return '#1DA1F2';
      case 'email': return '#EA4335';
      default: return colors.primary;
    }
  };

  const getDisplayName = () => {
    if (!contact && !conversation) return 'Loading...';
    return contact?.name || conversation?.contactName || 'Unknown Contact';
  };

  // 🔥 REFACTORED: Enhanced expiry logic to handle conversations just opened but not started
  // Handles 3 scenarios:
  // 1. New conversations (no messages) - treated as closed (need template messages)
  // 2. Conversations with inbound messages - 24-hour window based on last customer message
  // 3. Conversations with only outbound messages - 24-hour window based on conversation creation
  
  const isConversationClosed = () => {
    if (!conversation) {
      console.log('🔍 [ConversationScreen] No conversation data available');
      return false;
    }
    
    console.log('🔍 [ConversationScreen] Checking conversation window:', {
      status: conversation.status,
      lastMessageAt: conversation.lastMessageAt,
      contactLastMessageAt: conversation.contactLastMessageAt,
      messageCount: conversation.messageCount,
      channel: conversation.channel
    });
    
    // Check explicit status first (closed, archived, spam)
    if (conversation.status === 'closed' || conversation.status === 'archived' || conversation.status === 'spam') {
      console.log('🔴 [ConversationScreen] Conversation explicitly closed - status:', conversation.status);
      return true;
    }
    
    // 🔥 SCENARIO 1: New conversation (no messages at all) - needs template message
    if (!conversation.contactLastMessageAt && conversation.messageCount === 0) {
      console.log('🔴 [ConversationScreen] NEW conversation - no messages yet, needs template message');
      return true;
    }

    // 🔥 No contact last message but has messages = only outbound (or data missing). Window is closed until contact replies.
    if (!conversation.contactLastMessageAt && conversation.messageCount && conversation.messageCount > 0) {
      console.log('🔴 [ConversationScreen] 24-hour window CLOSED - no contact last message time (template/message tag only)');
      return true;
    }
    
    // 🔥 SCENARIO 2: Conversation with inbound messages - use 24-hour window from last customer message
    if (conversation.contactLastMessageAt) {
      const lastContactMessageDate = new Date(conversation.contactLastMessageAt);
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const hoursSinceLastContactMessage = (now.getTime() - lastContactMessageDate.getTime()) / (60 * 60 * 1000);
      
      console.log('🔍 [ConversationScreen] 24-hour window check (based on contact last message):', {
        contactLastMessageAt: conversation.contactLastMessageAt,
        lastContactMessageDate: lastContactMessageDate.toISOString(),
        twentyFourHoursAgo: twentyFourHoursAgo.toISOString(),
        hoursSinceLastContactMessage: hoursSinceLastContactMessage.toFixed(2),
        isExpired: lastContactMessageDate < twentyFourHoursAgo
      });
      
      // 24-hour window: conversation is closed if last CONTACT message was more than 24 hours ago
      if (lastContactMessageDate < twentyFourHoursAgo) {
        console.log('🔴 [ConversationScreen] 24-hour window CLOSED - messaging restricted to templates only (based on contact message)');
        return true;
      }
      
      console.log('🟢 [ConversationScreen] 24-hour window OPEN - messaging available (based on contact message)');
      return false;
    }
    
    // 🔥 SCENARIO 3: Conversation with only outbound messages (no inbound) - use lastMessageAt as fallback
    if (conversation.lastMessageAt) {
      const lastMessageDate = new Date(conversation.lastMessageAt);
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const hoursSinceLastMessage = (now.getTime() - lastMessageDate.getTime()) / (60 * 60 * 1000);
      
      console.log('🔍 [ConversationScreen] 24-hour window check (based on last message):', {
        lastMessageAt: conversation.lastMessageAt,
        lastMessageDate: lastMessageDate.toISOString(),
        twentyFourHoursAgo: twentyFourHoursAgo.toISOString(),
        hoursSinceLastMessage: hoursSinceLastMessage.toFixed(2),
        isExpired: lastMessageDate < twentyFourHoursAgo
      });
      
      // 24-hour window: conversation is closed if last message was more than 24 hours ago
      if (lastMessageDate < twentyFourHoursAgo) {
        console.log('🔴 [ConversationScreen] 24-hour window CLOSED - messaging restricted to templates only (based on last message)');
        return true;
      }
      
      console.log('🟢 [ConversationScreen] 24-hour window OPEN - messaging available (based on last message)');
      return false;
    }
    
    // Fallback - treat as closed if no timestamps available
    console.log('🔴 [ConversationScreen] No timestamps available - treating as closed');
    return true;
  };

  // Facebook Messenger only: human_agent tag allows replying between 24h and 7 days after last contact message.
  // After 7 days (or no previous contact message), Reopen button must not be shown.
  const canShowFacebookReopen = () => {
    if (conversation?.channel !== 'facebook-messenger') return false;
    if (!isConversationClosed()) return false;
    if (!conversation?.contactLastMessageAt) return false;
    const lastContactDate = new Date(conversation.contactLastMessageAt);
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const withinSevenDays = lastContactDate >= sevenDaysAgo;
    return withinSevenDays;
  };

  // 🔥 REFACTORED: Enhanced near-expiry logic to handle conversations just opened but not started
  // Handles 3 scenarios:
  // 1. New conversations (no messages) - not near expiry (already closed)
  // 2. Conversations with inbound messages - 22-24 hour window based on last customer message
  // 3. Conversations with only outbound messages - 22-24 hour window based on conversation creation
  
  const isConversationNearExpiry = () => {
    if (!conversation) return false;
    
    // 🔥 SCENARIO 1: New conversation (no messages at all) - not near expiry (already closed)
    if (!conversation.contactLastMessageAt && conversation.messageCount === 0) {
      console.log('🔍 [ConversationScreen] NEW conversation - not near expiry (already closed)');
      return false;
    }
    
    // 🔥 SCENARIO 2: Conversation with inbound messages - use 22-24 hour window from last customer message
    if (conversation.contactLastMessageAt) {
      const lastContactMessageDate = new Date(conversation.contactLastMessageAt);
      const now = new Date();
      const twentyTwoHoursAgo = new Date(now.getTime() - 22 * 60 * 60 * 1000);
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const hoursSinceLastContactMessage = (now.getTime() - lastContactMessageDate.getTime()) / (60 * 60 * 1000);
      
      console.log('🔍 [ConversationScreen] Near-expiry check (based on contact last message):', {
        contactLastMessageAt: conversation.contactLastMessageAt,
        lastContactMessageDate: lastContactMessageDate.toISOString(),
        twentyTwoHoursAgo: twentyTwoHoursAgo.toISOString(),
        twentyFourHoursAgo: twentyFourHoursAgo.toISOString(),
        hoursSinceLastContactMessage: hoursSinceLastContactMessage.toFixed(2),
        isNearExpiry: lastContactMessageDate < twentyTwoHoursAgo && lastContactMessageDate >= twentyFourHoursAgo
      });
      
      // Near expiry if last CONTACT message was between 22-24 hours ago
      return lastContactMessageDate < twentyTwoHoursAgo && lastContactMessageDate >= twentyFourHoursAgo;
    }
    
    // 🔥 SCENARIO 3: Conversation with only outbound messages (no inbound) - use lastMessageAt as fallback
    if (conversation.lastMessageAt) {
      const lastMessageDate = new Date(conversation.lastMessageAt);
      const now = new Date();
      const twentyTwoHoursAgo = new Date(now.getTime() - 22 * 60 * 60 * 1000);
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const hoursSinceLastMessage = (now.getTime() - lastMessageDate.getTime()) / (60 * 60 * 1000);
      
      console.log('🔍 [ConversationScreen] Near-expiry check (based on last message):', {
        lastMessageAt: conversation.lastMessageAt,
        lastMessageDate: lastMessageDate.toISOString(),
        twentyTwoHoursAgo: twentyTwoHoursAgo.toISOString(),
        twentyFourHoursAgo: twentyFourHoursAgo.toISOString(),
        hoursSinceLastMessage: hoursSinceLastMessage.toFixed(2),
        isNearExpiry: lastMessageDate < twentyTwoHoursAgo && lastMessageDate >= twentyFourHoursAgo
      });
      
      // Near expiry if last message was between 22-24 hours ago
      return lastMessageDate < twentyTwoHoursAgo && lastMessageDate >= twentyFourHoursAgo;
    }
    
    // Fallback - not near expiry if no timestamps available
    console.log('🔍 [ConversationScreen] No timestamps available - not near expiry');
    return false;
  };

  // 🔥 REFACTORED: Enhanced time until expiry logic to handle conversations just opened but not started
  // Handles 3 scenarios:
  // 1. New conversations (no messages) - no time left (already closed)
  // 2. Conversations with inbound messages - time based on last customer message
  // 3. Conversations with only outbound messages - time based on conversation creation
  
  const getTimeUntilExpiry = (): string => {
    if (!conversation) return '';
    
    // 🔥 SCENARIO 1: New conversation (no messages at all) - no time left (already closed)
    if (!conversation.contactLastMessageAt && conversation.messageCount === 0) {
      console.log('🔍 [ConversationScreen] NEW conversation - no time left (already closed)');
      return 'Expired';
    }
    
    // 🔥 SCENARIO 2: Conversation with inbound messages - use time based on last customer message
    if (conversation.contactLastMessageAt) {
      const lastContactMessageDate = new Date(conversation.contactLastMessageAt);
      const twentyFourHoursLater = new Date(lastContactMessageDate.getTime() + 24 * 60 * 60 * 1000);
      const now = new Date();
      const diffMs = twentyFourHoursLater.getTime() - now.getTime();
      
      console.log('🔍 [ConversationScreen] Time until expiry (based on contact last message):', {
        contactLastMessageAt: conversation.contactLastMessageAt,
        lastContactMessageDate: lastContactMessageDate.toISOString(),
        twentyFourHoursLater: twentyFourHoursLater.toISOString(),
        diffMs: diffMs,
        diffHours: Math.floor(diffMs / (60 * 60 * 1000)),
        diffMinutes: Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000))
      });
      
      if (diffMs <= 0) return 'Expired';
      
      const diffHours = Math.floor(diffMs / (60 * 60 * 1000));
      const diffMinutes = Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000));
      
      if (diffHours > 0) {
        return `${diffHours}h ${diffMinutes}m`;
      } else {
        return `${diffMinutes}m`;
      }
    }
    
    // 🔥 SCENARIO 3: Conversation with only outbound messages (no inbound) - use time based on lastMessageAt
    if (conversation.lastMessageAt) {
      const lastMessageDate = new Date(conversation.lastMessageAt);
      const twentyFourHoursLater = new Date(lastMessageDate.getTime() + 24 * 60 * 60 * 1000);
      const now = new Date();
      const diffMs = twentyFourHoursLater.getTime() - now.getTime();
      
      console.log('🔍 [ConversationScreen] Time until expiry (based on last message):', {
        lastMessageAt: conversation.lastMessageAt,
        lastMessageDate: lastMessageDate.toISOString(),
        twentyFourHoursLater: twentyFourHoursLater.toISOString(),
        diffMs: diffMs,
        diffHours: Math.floor(diffMs / (60 * 60 * 1000)),
        diffMinutes: Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000))
      });
      
      if (diffMs <= 0) return 'Expired';
      
      const diffHours = Math.floor(diffMs / (60 * 60 * 1000));
      const diffMinutes = Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000));
      
      if (diffHours > 0) {
        return `${diffHours}h ${diffMinutes}m`;
      } else {
        return `${diffMinutes}m`;
      }
    }
    
    // Fallback - no time left if no timestamps available
    console.log('🔍 [ConversationScreen] No timestamps available - no time left');
    return 'Expired';
  };

  const getPhoneNumber = () => {
    if (!contact && !conversation) return '';
    return contact?.phoneNumber || conversation?.phoneNumber || '';
  };

  const getOnlineStatus = () => {
    if (!contact) return false;
    return contact.isOnline;
  };

  const handleArchiveConversation = () => {
    Alert.alert(
      'Archive Conversation',
      'Are you sure you want to archive this conversation?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Archive', 
          style: 'destructive',
          onPress: async () => {
            try {
              const result = await apiService.authenticatedRequest<any>(`/api/whatsapp/chat/conversations/${conversationId}/archive`, {
                method: 'POST',
                body: JSON.stringify({
                  reason: 'User archived',
                  archivedBy: 'user',
                }),
              });

              if (result.statusCode === 1000) {
                showNotification('success', t('conversation.archived'), t('conversation.archivedDesc'));
                onNavigateBack();
              } else {
                showNotification('error', t('common.error'), t('conversation.archiveFailed'));
              }
            } catch (error) {
              console.error('Error archiving conversation:', error);
              showNotification('error', t('common.error'), t('conversation.archiveFailed'));
            }
          }
        }
      ]
    );
  };

  // Handle close right pane
  const handleCloseRightPane = () => {
    setRightPaneContent('empty');
  };

  const renderMessage = ({ item, index }: { item: Message; index: number }) => {
    const isLastMessage = index === messages.length - 1;
    const isFirstMessage = index === 0;
    const isNextMessageFromSameUser = index < messages.length - 1 && 
      messages[index + 1].direction === item.direction;
    const isPrevMessageFromSameUser = index > 0 && 
      messages[index - 1].direction === item.direction;
    
    // Calculate time difference for better grouping
    const timeDiff = index > 0 ? 
      new Date(item.timestamp).getTime() - new Date(messages[index - 1].timestamp).getTime() : 0;
    const isNewGroup = timeDiff > 5 * 60 * 1000; // 5 minutes
    const messageIdentifier = getStableMessageId(item);
    const isHighlighted = !!(highlightedMessageId && messageIdentifier && highlightedMessageId === messageIdentifier);
    
    const isOutbound = item.direction === 'outbound' || item.direction === 'outbound-api';
    const maxBubbleWidth = messagesPaneWidth ? Math.floor(messagesPaneWidth * 0.7) : undefined;
    // On web with dir="rtl", flex-end/flex-start cross-axis meaning is flipped by CSS.
    // flex-start = physical right, flex-end = physical left. So swap for web RTL.
    const webRTLFlip = Platform.OS === 'web' && isRTL;
    // List is newest-first: the next index is the older neighbour, so a day change there starts a new day here.
    const olderMessage = messages[index + 1];
    const dayKey = (value: string) => new Date(value).toDateString();
    const startsNewDay = !olderMessage || dayKey(olderMessage.timestamp) !== dayKey(item.timestamp);
    const dayLabel = (() => {
      if (!startsNewDay) return '';
      const date = new Date(item.timestamp);
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      if (date.toDateString() === today.toDateString()) return t('chat.today');
      if (date.toDateString() === yesterday.toDateString()) return t('chat.yesterday');
      return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
    })();
    return (
      <View style={[
        styles.messageWrapper,
        isOutbound
          ? (webRTLFlip ? styles.messageWrapperInbound : styles.messageWrapperOutbound)
          : (webRTLFlip ? styles.messageWrapperOutbound : styles.messageWrapperInbound),
        isFirstMessage && styles.firstMessage,
        isNewGroup && styles.newGroupMessage
      ]}>
        {startsNewDay ? (
          <View style={styles.daySeparator}>
            <Text style={styles.daySeparatorText}>{dayLabel}</Text>
          </View>
        ) : null}
        <MessageBubble
          message={item}
          isLastInGroup={!isNextMessageFromSameUser}
          showTimestamp={isLastMessage || !isNextMessageFromSameUser || isNewGroup}
          onReply={handleReply}
          onCopy={handleCopy}
          onShare={handleShareMessage}
          onForward={handleForward}
          onScrollToMessage={handleScrollToMessage}
          isHighlighted={isHighlighted}
          fallbackContactName={getDisplayName()}
          businessName={currentBusiness?.name}
          conversationId={conversationId}
          maxBubbleWidth={maxBubbleWidth}
          onRetry={handleRetryMessage}
          onSetContactLocation={handleSetContactLocationFromMessage}
          onCreateOrderAtLocation={handleCreateOrderAtMessageLocation}
        />
        {/* Quick Reply Button - only show on long press or for last message */}
      </View>
    );
  };

  // Debug web padding
  if (Platform.OS === 'web') {
    console.log('🌐 [ConversationScreen] Web detected, should have paddingTop: 180');
  }

  const looksLikePhoneNumber = useCallback((value?: string | null) => {
    if (!value || typeof value !== 'string') {
      return false;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return false;
    }
    return /^[+]?[-()\s\d]{6,}$/.test(trimmed);
  }, []);

  const resolveContactLabel = useCallback((value?: string | null) => {
    if (!value || typeof value !== 'string') {
      return undefined;
    }
    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === 'unknown contact') {
      return undefined;
    }
    if (looksLikePhoneNumber(trimmed)) {
      return undefined;
    }
    return trimmed;
  }, [looksLikePhoneNumber]);

  const replyPreviewMessage = useMemo(() => {
    if (!replyingTo) {
      return null;
    }

    const headerDisplayName = getDisplayName();
    const preferredName =
      replyingTo.direction === 'outbound' || replyingTo.direction === 'outbound-api'
        ? 'You'
        : (resolveContactLabel(replyingTo.contactName) || resolveContactLabel(headerDisplayName) || headerDisplayName || 'Contact');

    return {
      ...replyingTo,
      contactName: preferredName,
    } as Message;
  }, [replyingTo, resolveContactLabel]);

  // The app-level nav/notifications <Header> — never rendered in embedded
  // mode (the host screen's own shell already has one); full-screen mode
  // keeps this exactly as before.
  const appHeader = !embedded && Platform.OS === 'web' ? (
    <Header
      showNotifications={true}
      showMessages={true}
      onNotificationPress={() => navigateTo('notifications')}
      onMessagePress={() => navigateTo('chat')}
      showNavigation={Platform.OS === 'web'}
      activeTab="chat"
      onTabPress={(screenId) => {
        // Map screen IDs to tab IDs for active state
        const tabId = screenId === 'business-calendar' ? 'calendar' : screenId;
        setActiveTab(tabId);

        // Handle navigation for all tabs
        switch (screenId) {
          case 'home':
            navigateTo('home');
            break;
          case 'contacts':
            navigateTo('contacts');
            break;
          case 'business-calendar':
            // Calendar tab sends 'business-calendar' from Header component
            navigateTo('business-calendar');
            break;
          case 'orders':
            navigateTo('orders');
            break;
          case 'chat':
            // Already on conversation screen
            break;
          case 'social':
            navigateTo('social');
            break;
          case 'performance':
            navigateTo('performance');
            break;
          default:
            break;
        }
      }}
    />
  ) : undefined;

  // Everything that used to be ScreenLayout's `children` — built once here so
  // it can either be placed inside this screen's own <ScreenLayout> (full-
  // screen mode, unchanged) or returned directly, bypassing ScreenLayout
  // entirely (embedded mode — see the embedded prop's own doc comment for
  // why: ScreenLayout independently renders its own StatusBar/safe-area/
  // LeftPane/RightPane, which would nest a second full chrome stack inside
  // the HOST screen's own pane).
  const innerContent = (
    <>
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : Platform.OS === 'web' ? 'height' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'web' ? 0 : keyboardOffset}
        enabled={Platform.OS !== 'web'}
      >
        {!embedded && (
          <Animated.View
            style={[
              styles.header,
              Platform.OS === 'web' && {
                top: appHeaderHeight,
                left: physicalLeftInsetAnim,
                right: physicalRightInsetAnim,
              } as any,
            ]}
          >
        <View style={[styles.headerInner, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity style={[styles.backButton, rtl.marginEnd(designTokens.spacing.sm)]} onPress={onNavigateBack}>
              <Ionicons name={rtl.mirrorIcon('chevron-back') as any} size={24} color={colors.textDark} />
            </TouchableOpacity>

            <View style={[styles.headerCenter, { flexDirection: rtl.rowDirection }]}>
              {/* Contact Avatar */}
              <View style={[styles.avatarContainer, rtl.marginEnd(designTokens.spacing.md)]}>
                <AvatarWithProviderBadge
                  name={getDisplayName()}
                  provider={conversation ? getProviderFromConversation(conversation) : 'unknown'}
                  imageUrl={conversation?.profilePictureUrl}
                  size={44}
                />
              </View>

              {/* Contact Info */}
              <View style={[styles.contactInfo, { alignItems: 'flex-start' }]}>
                <Text style={[styles.contactName, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.semibold }]} numberOfLines={1}>
                  {getDisplayName()}
                </Text>
                <Text style={[styles.contactPhone, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]} numberOfLines={1}>
                  {getPhoneNumber()}
                </Text>
                {getOnlineStatus() && (
                  <View style={[styles.onlineIndicator, { flexDirection: rtl.rowDirection }]}>
                    <View style={[styles.onlineDot, rtl.marginEnd(6)]} />
                    <Text style={styles.onlineText}>{t('conversation.online')}</Text>
                  </View>
                )}
              </View>
            </View>

          <View style={styles.headerActions}>
            <TouchableOpacity
              ref={groupActionsButtonRef as any}
              style={styles.groupActionsButton}
              onPress={openGroupActionsMenu}
              activeOpacity={0.7}
              accessibilityLabel={t('conversation.groupActions')}
            >
              <View style={styles.groupActionsIconWrap}>
                <Ionicons name="ellipsis-horizontal" size={18} color={colors.primary} />
                {hasUnseenNotes && <View style={styles.unseenNotesDot} />}
              </View>
            </TouchableOpacity>
          </View>
        </View>
          </Animated.View>
        )}


        {/* Banners between the header and the message list — wrapped together
            so the 80px clearance the fixed conversation header needs (see
            styles.header's position:'fixed' on web, and messagesContainer's
            own hardcoded marginTop:'80px' below) is applied exactly ONCE,
            regardless of how many banners are actually showing. Without this
            wrapper, a banner rendered directly in flow here starts at y:0
            (behind the fixed header, since fixed positioning takes it out of
            flow) instead of visually below it — confirmed bug via production
            testing (2026-09-10): the engagement cooldown banner was present
            in the DOM/API response but rendered hidden under the header. */}
        {!loading && (isConversationNearExpiry() || conversation?.engagementCooldown?.onCooldown || showDeliveryRetry) && (
          <View style={!embedded && Platform.OS === 'web' ? ({ marginTop: 80 } as any) : undefined}>
            {/* Near-Expiry Warning Banner */}
            {isConversationNearExpiry() && (
              <View style={[styles.nearExpiryBanner, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="warning" size={20} color="#FFA726" />
                <View style={[styles.nearExpiryTextContainer, { alignItems: 'flex-start' }]}>
                  <Text style={[styles.nearExpiryText, { textAlign: rtl.textStart }]}>
                    Messaging window closing soon
                  </Text>
                  <Text style={[styles.nearExpiryCountdown, { textAlign: rtl.textStart }]}>
                    {getTimeUntilExpiry()} remaining
                  </Text>
                </View>
              </View>
            )}

            {/* Engagement Cooldown Banner — this contact recently hit Meta's
                "healthy ecosystem engagement" delivery throttle (error 131049).
                Shown proactively on open, not just after a failed send attempt,
                so an agent sees it before trying (and, per the 2026-09-08
                incident, before repeated retries make the suppression last even
                longer). Mirrors nearExpiryBanner's structure with a red palette
                since this is a harder stop than "window closing soon". */}
            {(conversation?.engagementCooldown?.onCooldown || showDeliveryRetry) && (
              <View style={[styles.cooldownBanner, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="alert-circle" size={20} color="#E53935" />
                <View style={[styles.nearExpiryTextContainer, { alignItems: 'flex-start' }]}>
                  <Text style={[styles.cooldownText, { textAlign: rtl.textStart }]}>
                    {t(conversation?.engagementCooldown?.onCooldown ? 'conversation.engagementCooldownTitle' : 'conversation.deliveryRetry.title')}
                  </Text>
                  <Text style={[styles.cooldownSubtext, { textAlign: rtl.textStart }]}>
                    {t('conversation.engagementCooldownSubtext')}
                  </Text>
                  {deliveryRetry.retry ? <Text style={[styles.cooldownSubtext, { textAlign: rtl.textStart }]}>
                    {t(`conversation.deliveryRetry.state.${deliveryRetry.retry.state}` as any)}
                    {deliveryRetry.retry.nextAttemptAt ? ` · ${t('conversation.deliveryRetry.next')}: ${new Date(deliveryRetry.retry.nextAttemptAt).toLocaleString(locale)}` : ''}
                  </Text> : null}
                  {deliveryRetry.retry?.lastError || deliveryRetry.error ? <Text style={[styles.cooldownSubtext, { textAlign: rtl.textStart }]}>{deliveryRetry.error || deliveryRetry.retry?.lastError}</Text> : null}
                  <TouchableOpacity
                    onPress={() => void deliveryRetry.configure(!deliveryRetry.retry?.enabled)}
                    disabled={deliveryRetry.busy}
                    accessibilityRole="button"
                    style={{ paddingVertical: spacing.sm, minHeight: 44, justifyContent: 'center' }}
                  >
                    {deliveryRetry.busy ? <ActivityIndicator size="small" color={colors.primary as string} /> : <Text style={{ color: colors.primary as string, textDecorationLine: 'underline', fontFamily: typography.fontFamily.semibold }}>
                      {t(deliveryRetry.retry?.enabled ? 'conversation.deliveryRetry.stop' : 'conversation.deliveryRetry.enable')}
                    </Text>}
                  </TouchableOpacity>
                  <Text style={[styles.cooldownSubtext, { textAlign: rtl.textStart }]}>{t('conversation.deliveryRetry.hint')}</Text>
                </View>
              </View>
            )}
          </View>
        )}

        {/* Loading State */}
        {loading && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={styles.loadingText}>Loading messages...</Text>
          </View>
        )}

        {/* Messages List - Fixed height container for web */}
        {!loading && (
          <View
            style={[
              embedded ? styles.messagesContainerEmbedded : styles.messagesContainer,
              !embedded && Platform.OS === 'web' && {
                height: `calc(100vh - ${appHeaderHeight + 200}px)` as any,
                maxHeight: `calc(100vh - ${appHeaderHeight + 200}px)` as any,
                // The 80px header clearance is already applied once by the
                // banner wrapper above when a banner is showing — without
                // this override, messagesContainer's own hardcoded
                // marginTop:'80px' (see its base style) would stack on top
                // of that and double the gap.
                ...((isConversationNearExpiry() || conversation?.engagementCooldown?.onCooldown || showDeliveryRetry) && { marginTop: 0 }),
              },
            ]}
          >
          <View
            style={styles.messagesContainerInner}
            onLayout={(event) => {
              const nextWidth = event.nativeEvent.layout.width;
              if (nextWidth > 0 && Math.abs(nextWidth - (messagesPaneWidth || 0)) > 1) {
                setMessagesPaneWidth(nextWidth);
              }
            }}
          >
            <FlatList
              ref={flatListRef}
              inverted
              data={messages}
              renderItem={renderMessage}
              keyExtractor={(item) => item._id}
              style={styles.messagesList}
              contentContainerStyle={styles.messagesContent}
              showsVerticalScrollIndicator={false}
              maintainVisibleContentPosition={{
                minIndexForVisible: 0,
                autoscrollToTopThreshold: 10,
              }}
              onContentSizeChange={() => {
                if (shouldScrollToBottomRef.current && !loadingMore) {
                  shouldScrollToBottomRef.current = false;
                  hasScrolledToEndOnLoadRef.current = true;
                  setTimeout(() => scrollToBottom(false), 50);
                }
              }}
              onEndReached={() => {
                if (hasMoreMessages && !loadingMore) {
                  loadMoreMessages();
                }
              }}
              onEndReachedThreshold={0.2}
              // Performance optimizations
              removeClippedSubviews={false}
              maxToRenderPerBatch={5}
              updateCellsBatchingPeriod={100}
              initialNumToRender={10}
              windowSize={5}
              ListHeaderComponent={() => (
                <TypingIndicator
                  isVisible={isTyping}
                  userName={typingUser || conversation?.contactName || 'Contact'}
                />
              )}
              ListEmptyComponent={
                <View style={styles.emptyState}>
                  <Ionicons name="chatbubbles-outline" size={64} color={colors.textLight} />
                  <Text style={styles.emptyStateText}>No messages yet</Text>
                  <Text style={styles.emptyStateSubtext}>
                    Start a conversation with {getDisplayName()}
                  </Text>
                </View>
              }
              ListFooterComponent={
                loadingMore ? (
                  <View style={[styles.loadingMoreContainer, { flexDirection: rtl.rowDirection }]}>
                    <ActivityIndicator size="small" color={colors.primary} />
                    <Text style={[styles.loadingMoreText, rtl.marginStart(designTokens.spacing.sm)]}>{t('conversation.loadingMoreMessages')}</Text>
                  </View>
                ) : null
              }
            />
          </View>
          </View>
        )}


        {/* Enhanced Message Input with Safe Area for Android — a normal flex
            child when embedded, instead of fixed to the browser viewport. */}
        <Animated.View
          style={[
            embedded ? styles.inputContainerEmbedded : styles.inputContainer,
            !embedded && Platform.OS === 'web' && { left: physicalLeftInsetAnim, right: physicalRightInsetAnim } as any,
          ]}
        >
        <View style={[styles.inputContainerInner, Platform.OS === 'android' && { paddingBottom: insets.bottom }]}>
          {!conversation?.engagementCooldown?.onCooldown && !['closed', 'archived', 'spam'].includes(conversation?.status || '') && <AiConversationAssistant
            key={assistantScope}
            conversationId={conversationId}
            contextVersion={messages.find(m => m.direction === 'inbound')?.messageId || conversation?.contactLastMessageAt || 'first-contact'}
            contextReady={!loading && !!conversation}
            disabled={sending}
            onUseReply={text => { setPendingAiChoices([]); setComposerText(text); }}
            onUseChoices={(text, choices) => { setComposerText(text); setPendingAiChoices(choices); }}
            onSendNow={(text, choices) => { void sendAiRecommendation(text, choices); }}
            onSendMedia={media => {
              void apiService.sendAiMedia(conversationId, media, aiCustomerLanguage(messages, '').startsWith('ar') ? 'ar' : 'en')
                .then(res => { if (res.statusCode !== 1000) throw new Error((res.errors as any)?.[0]?.message || (res.errors as any)?.[0] || t('ai.actionFailed')); })
                .catch((error: any) => showNotification('error', t('common.error'), error?.message || t('ai.actionFailed')));
            }}
            sentVersion={sentVersion}
            onSelectTemplate={id => { void prepareAiTemplate(id).catch(() => showNotification('error', t('common.error'), t('ai.actionFailed'))); }}
            onAction={(type, ids) => { void prepareAiDocument(type, ids).catch(() => showNotification('error', t('common.error'), t('ai.actionFailed'))); }}
          />}
          {!!conversation && <OpenCartsCard
            conversationId={conversationId}
            refreshKey={messages[messages.length - 1]?.messageId}
            canOrder={hasPermissionAtLeast('general.manageOrders', 'edit')}
            canQuote={hasPermissionAtLeast('general.manageOrders', 'edit')}
            canEdit
            onCreate={(type, lines) => { void prepareAiDocument(type, lines).catch(() => showNotification('error', t('common.error'), t('ai.actionFailed'))); }}
          />}
          {!!pendingAiChoices.length && <ChoiceDraftEditor choices={pendingAiChoices} onChange={setPendingAiChoices} />}
          {replyPreviewMessage && (
            <View style={styles.replyContext}>
              <ReplyIndicator
                message={replyPreviewMessage}
                onCancel={cancelReply}
                showCancelButton={true}
                fallbackContactName={getDisplayName()}
              />
            </View>
          )}
          {(() => {
            // Engagement cooldown: the header banner above already explains
            // why, so just hide the input entirely here rather than
            // duplicating the explanation in a second block — the backend
            // guard (WhatsAppChatService/WhatsAppController) also refuses
            // the send regardless, this just prevents the attempt from
            // being made client-side in the first place.
            if (conversation?.engagementCooldown?.onCooldown) {
              return null;
            }
            const isClosed = isConversationClosed();
            console.log('🔍 [ConversationScreen] isConversationClosed() result:', isClosed);
            if (!isClosed) {
              return (
                <MessageInputWithVoice
                  onShareDocument={hasPermissionAtLeast('general.manageOrders', 'view') ? (type) => { void openShareDocument(type); } : undefined}
                  onAddOptions={() => setPendingAiChoices(prev => prev.length ? prev : [newChoice(), newChoice()])}
                  onSendMessage={(content, type, mediaUri, duration, location, metadata, options) => {
                    // The composer is controlled here, so clearing it after a send is ours to do.
                    if (!type || type === 'text' || type === 'interactive') setComposerText('');
                    const readyChoices = pendingAiChoices.filter(c => c.label.trim());
                    if ((!type || type === 'text') && readyChoices.length && conversation) {
                      setPendingAiChoices([]);
                      const packet = buildAiChoiceMessage(conversation.channel, content, readyChoices.map(c => ({ ...c, label: c.label.trim() })), aiCustomerLanguage(messages, content));
                      return sendMessage(packet.content, packet.type, mediaUri, duration, location, metadata,
                        { ...options, aiChoiceDraft: true, ...(packet.interactive ? { interactive: packet.interactive, interactiveType: packet.interactive.type } : {}) });
                    }
                    return sendMessage(content, type, mediaUri, duration, location, metadata, options);
                  }}
                  value={composerText}
                  onChangeText={setComposerText}
                  sending={sending}
                  placeholder={t('conversation.messagePlaceholderWithName', { name: getDisplayName() })}
                  horizontalInset={paneHorizontalInset}
                  conversationId={conversationId}
                  embedded={embedded}
                />
              );
            }
            // Truly closed — no action possible
            if (conversation?.status === 'closed' || conversation?.status === 'archived' || conversation?.status === 'spam') {
              return (
                <View style={[styles.conversationClosedContainer, { flexDirection: rtl.rowDirection }]}>
                  <View style={[styles.conversationClosedIcon, rtl.marginEnd(designTokens.spacing.md)]}>
                    <Ionicons name="lock-closed" size={24} color={colors.text.secondary} />
                  </View>
                  <View style={[styles.conversationClosedTextContainer, { alignItems: 'flex-start' }]}>
                    <Text style={[styles.conversationClosedText, { textAlign: rtl.textStart }]}>
                      {conversation?.status === 'closed' ? t('chat.closeConversation') :
                       conversation?.status === 'archived' ? t('conversation.discussionArchived') :
                       t('conversation.discussionSpam')}
                    </Text>
                    <Text style={[styles.conversationClosedSubtext, { textAlign: rtl.textStart }]}>{t('conversation.noNewMessages')}</Text>
                  </View>
                </View>
              );
            }
            // Facebook window expired beyond reopenable range
            if (conversation?.channel === 'facebook-messenger' && !canShowFacebookReopen()) {
              return (
                <View style={[styles.conversationClosedContainer, { flexDirection: rtl.rowDirection }]}>
                  <View style={[styles.conversationClosedIcon, rtl.marginEnd(designTokens.spacing.md)]}>
                    <Ionicons name="lock-closed" size={24} color={colors.text.secondary} />
                  </View>
                  <View style={[styles.conversationClosedTextContainer, { alignItems: 'flex-start' }]}>
                    <Text style={[styles.conversationClosedText, { textAlign: rtl.textStart }]}>{t('conversation.windowClosed')}</Text>
                    <Text style={[styles.conversationClosedSubtext, { textAlign: rtl.textStart }]}>{t('conversation.cannotReopen')}</Text>
                  </View>
                </View>
              );
            }
            // WhatsApp or Facebook (reopenable) — silent input-bar style button
            return (
              <TouchableOpacity
                style={[styles.templateInputBar, { flexDirection: rtl.rowDirection }]}
                activeOpacity={0.7}
                onPress={conversation?.channel === 'whatsapp'
                  ? () => {
                      setAiTemplateId(undefined);
                      resolvePhoneForSend().then(setResolvedReopenPhone);
                      setShowReopenTemplateModal(true);
                    }
                  : () => setShowFacebookReopenModal(true)}
              >
                <Ionicons name="chatbubble-ellipses-outline" size={20} color={colors.text.secondary} />
                <Text style={[styles.templateInputBarText, { textAlign: rtl.textStart }]}>{t('conversation.sendAMessage')}</Text>
                <Ionicons name="send" size={18} color={colors.primary} style={isRTL ? { marginRight: 'auto' as any, transform: [{ scaleX: -1 }] } : { marginLeft: 'auto' as any }} />
              </TouchableOpacity>
            );
          })()}
        </View>
        </Animated.View>

        {/* Active Assignment Overlay - positioned absolutely to cover conversation body.
            Embedded mode has no fixed header gap to clear (the contact-info
            bar above is a normal in-flow flex child), so top:0 instead of
            the hardcoded full-screen header-height guess. */}
        {currentAssignment?.status === 'active' && (
          <TouchableOpacity
            style={[styles.activeAssignmentAbsoluteOverlay, embedded && { top: 0 }]}
            onPress={handleStartAssignment}
            activeOpacity={0.8}
            pointerEvents="auto"
          >
            <View style={styles.activeAssignmentContent}>
              <Ionicons name="play-circle" size={48} color={colors.white} />
              <Text style={styles.activeAssignmentText}>{t('assignment.overlay.startTitle')}</Text>
              <Text style={styles.activeAssignmentSubtext}>{t('assignment.overlay.startSubtitle')}</Text>
            </View>
          </TouchableOpacity>
        )}
      </KeyboardAvoidingView>

      <Modal
        visible={showGroupActionsMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowGroupActionsMenu(false)}
      >
        <TouchableOpacity
          style={styles.assignmentMenuOverlay}
          activeOpacity={1}
          onPress={() => setShowGroupActionsMenu(false)}
        >
          <View
            style={[
              styles.assignmentMenu,
              groupActionsMenuAnchor
                ? {
                    top: groupActionsMenuAnchor.y + groupActionsMenuAnchor.height + 6,
                    left: Math.max(
                      12,
                      Math.min(
                        groupActionsMenuAnchor.x + groupActionsMenuAnchor.width - 200,
                        screenWidth - 212
                      )
                    ),
                    minWidth: 200,
                  }
                : { top: designTokens.spacing.xl * 2, right: designTokens.spacing.md, minWidth: 200 }
            ]}
          >
            <TouchableOpacity
              style={[styles.assignmentMenuItem, { flexDirection: rtl.rowDirection }]}
              onPress={handleViewContact}
              disabled={loadingViewedContact}
            >
              <Ionicons name="person-circle-outline" size={16} color={colors.primary} />
              <Text style={[styles.assignmentMenuText, rtl.marginStart(designTokens.spacing.sm)]}>
                {t('conversation.viewContact')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.assignmentMenuItem, { flexDirection: rtl.rowDirection }]}
              onPress={handleCopyNumber}
            >
              <Ionicons name="copy-outline" size={16} color={colors.primary} />
              <Text style={[styles.assignmentMenuText, rtl.marginStart(designTokens.spacing.sm)]}>
                {t('conversation.copyNumber')}
              </Text>
            </TouchableOpacity>

            {hasPhoneService && (
              <TouchableOpacity
                style={[styles.assignmentMenuItem, { flexDirection: rtl.rowDirection }]}
                onPress={handleCallContact}
              >
                <Ionicons name="call-outline" size={16} color={colors.primary} />
                <Text style={[styles.assignmentMenuText, rtl.marginStart(designTokens.spacing.sm)]}>
                  {t('conversation.call')}
                </Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.assignmentMenuItem, { flexDirection: rtl.rowDirection }]}
              onPress={handleOpenAssignTo}
              disabled={assignmentActionLoading}
            >
              <Ionicons name="person-add-outline" size={16} color={colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={[styles.assignmentMenuText, rtl.marginStart(designTokens.spacing.sm)]}>
                  {t('conversation.assignTo')}
                </Text>
                <Text style={[styles.assignmentMenuSubtext, rtl.marginStart(designTokens.spacing.sm)]} numberOfLines={1}>
                  {activeAssignment?.assignedTo && typeof activeAssignment.assignedTo === 'object'
                    ? t('conversation.assignedTo', {
                        name: `${activeAssignment.assignedTo.firstName || ''} ${activeAssignment.assignedTo.lastName || ''}`.trim(),
                      })
                    : t('conversation.unassigned')}
                </Text>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.assignmentMenuItem, { flexDirection: rtl.rowDirection }]}
              onPress={handleOpenNotes}
            >
              <View style={styles.groupActionsIconWrap}>
                <Ionicons name="document-text-outline" size={16} color={colors.primary} />
                {hasUnseenNotes && <View style={styles.unseenNotesDot} />}
              </View>
              <Text style={[styles.assignmentMenuText, rtl.marginStart(designTokens.spacing.sm)]}>
                {t('conversation.notes')}
              </Text>
              {hasUnseenNotes && (
                <Text style={[styles.assignmentMenuSubtext, rtl.marginStart(designTokens.spacing.sm)]}>
                  {t('conversation.newNotesIndicator')}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      <Modal
        visible={!!phonePickerNumbers}
        transparent
        animationType="fade"
        onRequestClose={() => setPhonePickerNumbers(null)}
      >
        <TouchableOpacity
          style={styles.assignmentMenuOverlay}
          activeOpacity={1}
          onPress={() => setPhonePickerNumbers(null)}
        >
          <View
            style={[
              styles.assignmentMenu,
              { top: designTokens.spacing.xl * 2, right: designTokens.spacing.md, minWidth: 200 },
            ]}
          >
            {(phonePickerNumbers || []).map(({ value, index }) => (
              <TouchableOpacity
                key={index}
                style={[styles.assignmentMenuItem, { flexDirection: rtl.rowDirection }]}
                onPress={() => handlePhoneIdentifierPick(index)}
              >
                <Ionicons name="call-outline" size={16} color={colors.primary} />
                <Text style={[styles.assignmentMenuText, rtl.marginStart(designTokens.spacing.sm)]} numberOfLines={1}>
                  {value}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      <Modal
        visible={showAssignmentActionsMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAssignmentActionsMenu(false)}
      >
        <TouchableOpacity
          style={styles.assignmentMenuOverlay}
          activeOpacity={1}
          onPress={() => setShowAssignmentActionsMenu(false)}
        >
          <View
            style={[
              styles.assignmentMenu,
              assignmentMenuAnchor
                ? {
                    top: assignmentMenuAnchor.y + assignmentMenuAnchor.height + 6,
                    left: Math.max(
                      12,
                      Math.min(
                        assignmentMenuAnchor.x + assignmentMenuAnchor.width - 180,
                        screenWidth - 192
                      )
                    ),
                  }
                : { top: designTokens.spacing.xl * 2, right: designTokens.spacing.md }
            ]}
          >
            <TouchableOpacity
              style={[
                styles.assignmentMenuItem,
                { flexDirection: rtl.rowDirection },
                !canStartAssignment && styles.assignmentMenuItemDisabled
              ]}
              onPress={() => {
                setShowAssignmentActionsMenu(false);
                handleStartAssignment();
              }}
              disabled={!canStartAssignment || assignmentActionLoading}
            >
              <Ionicons name="play" size={16} color={canStartAssignment ? colors.primary : colors.textLight} />
              <Text
                style={[
                  styles.assignmentMenuText,
                  rtl.marginStart(designTokens.spacing.sm),
                  !canStartAssignment && styles.assignmentMenuTextDisabled
                ]}
              >
                {t('assignment.action.start')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.assignmentMenuItem,
                { flexDirection: rtl.rowDirection },
                !canTransferAssignment && styles.assignmentMenuItemDisabled
              ]}
              onPress={() => {
                setShowAssignmentActionsMenu(false);
                setShowTransferModal(true);
              }}
              disabled={!canTransferAssignment || assignmentActionLoading}
            >
              <Ionicons name="swap-horizontal" size={16} color={canTransferAssignment ? colors.primary : colors.textLight} />
              <Text
                style={[
                  styles.assignmentMenuText,
                  rtl.marginStart(designTokens.spacing.sm),
                  !canTransferAssignment && styles.assignmentMenuTextDisabled
                ]}
              >
                {t('assignment.action.transfer')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.assignmentMenuItem,
                { flexDirection: rtl.rowDirection },
                !canCompleteAssignment && styles.assignmentMenuItemDisabled
              ]}
              onPress={() => {
                setShowAssignmentActionsMenu(false);
                handleCompleteAssignment();
              }}
              disabled={!canCompleteAssignment || assignmentActionLoading}
            >
              <Ionicons name="checkmark" size={16} color={canCompleteAssignment ? colors.success : colors.textLight} />
              <Text
                style={[
                  styles.assignmentMenuText,
                  rtl.marginStart(designTokens.spacing.sm),
                  !canCompleteAssignment && styles.assignmentMenuTextDisabled
                ]}
              >
                {t('assignment.action.complete')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.assignmentMenuItem,
                { flexDirection: rtl.rowDirection },
                !canCancelAssignment && styles.assignmentMenuItemDisabled
              ]}
              onPress={() => {
                setShowAssignmentActionsMenu(false);
                handleCancelAssignment();
              }}
              disabled={!canCancelAssignment || assignmentActionLoading}
            >
              <Ionicons name="close" size={16} color={canCancelAssignment ? colors.error : colors.textLight} />
              <Text
                style={[
                  styles.assignmentMenuText,
                  rtl.marginStart(designTokens.spacing.sm),
                  !canCancelAssignment && styles.assignmentMenuTextDisabled
                ]}
              >
                {t('assignment.action.cancel')}
              </Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Edit-contact fallback for native/mobile-web — ScreenLayout only
          renders the right pane (where rightPaneContent='edit-contact'
          normally shows ContactEditForm) on desktop web, so without this,
          tapping "View/Edit Contact" on native or a narrow mobile-web
          viewport opened no UI at all. Mirrors ContactsListScreen.tsx's
          same fallback pattern for its own edit-contact flow. */}
      {(Platform.OS !== 'web' || isMobileBrowser) && (
        <AppModal
          visible={rightPaneContent === 'edit-contact' && !!viewedContact}
          onClose={() => {
            setRightPaneContent('empty');
            setViewedContact(null);
          }}
          title={viewedContact?.name || t('conversation.viewContact')}
          size="large"
          scrollable
        >
          {viewedContact && (
            <ContactEditForm
              contact={viewedContact}
              onClose={() => {
                setRightPaneContent('empty');
                setViewedContact(null);
              }}
              onSave={(updated: any) => {
                setViewedContact(updated);
                showNotification('success', t('common.success'), t('common.save'));
                setRightPaneContent('empty');
                setViewedContact(null);
              }}
              onDelete={() => {
                setRightPaneContent('empty');
                setViewedContact(null);
              }}
              compact={false}
            />
          )}
        </AppModal>
      )}

      <UserAssignmentModal
        visible={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        contactId={conversation?.contactId || contact?.id || ''}
        parentContactId={(conversation as any)?.parentContactId || (contact as any)?.parentContactId || tempParentContactId || undefined}
        contactName={getDisplayName()}
        currentAssignment={currentAssignment}
        mode="select"
        titleOverride={t('assignment.transfer.title')}
        subtitleOverride={t('assignment.transfer.subtitle')}
        onAssignmentChange={() => undefined}
        onSelection={handleTransferSelection}
      />

      {/* Full reassignment modal — gated by contacts.manageAssignments, not role */}
      <UserAssignmentModal
        visible={showAssignmentModal && canManageContactAssignments}
        onClose={() => setShowAssignmentModal(false)}
        contactId={conversation?.contactId || contact?.id || ''}
        parentContactId={(conversation as any)?.parentContactId || (contact as any)?.parentContactId || tempParentContactId || undefined}
        contactName={getDisplayName()}
        currentAssignment={null}
        mode="assign"
        titleOverride={t('assignment.manage.title')}
        subtitleOverride={t('assignment.manage.subtitle')}
        onAssignmentChange={() => refreshAssignments()}
        onSelection={undefined}
      />

      <Modal
        visible={showNotesModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowNotesModal(false)}
      >
        <TouchableOpacity
          style={styles.notesModalOverlay}
          activeOpacity={1}
          onPress={() => setShowNotesModal(false)}
        >
          <Animated.View
            style={[
              styles.notesModalCard,
              Platform.OS === 'web' && { marginLeft: physicalLeftInsetAnim, marginRight: physicalRightInsetAnim } as any,
            ]}
          >
          <TouchableOpacity activeOpacity={1} style={styles.notesModalCardInner} onPress={(e) => e.stopPropagation()}>
            <View style={styles.notesModalGrabber} />
            <View style={[styles.notesModalHeader, { flexDirection: rtl.rowDirection }]}>
              <View style={[styles.notesModalTitleRow, { flexDirection: rtl.rowDirection }]}>
                <Text style={styles.notesModalTitle}>{t('conversation.notesTitle')}</Text>
                {contactNotes.length > 0 && (
                  <View style={styles.notesModalCountBadge}>
                    <Text style={styles.notesModalCountText}>{contactNotes.length}</Text>
                  </View>
                )}
              </View>
              <TouchableOpacity onPress={() => setShowNotesModal(false)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Ionicons name="close" size={22} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            {loadingNotes ? (
              <View style={styles.notesModalLoading}>
                <ActivityIndicator size="small" color={colors.primary} />
              </View>
            ) : (
              <ScrollView
                ref={notesScrollRef}
                style={styles.notesModalScroll}
                contentContainerStyle={styles.notesModalScrollContent}
                keyboardShouldPersistTaps="handled"
                onContentSizeChange={() => notesScrollRef.current?.scrollToEnd({ animated: false })}
              >
                <NotesSection
                  notes={[...contactNotes].sort(
                    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                  )}
                  onAddNote={handleAddNote}
                  onDeleteNote={canManageNotes ? handleDeleteNote : undefined}
                  currentUserId={user?.id}
                  showHeader={false}
                  editable={false}
                />
              </ScrollView>
            )}
            {/* Fixed add-note bar — stays put while the notes list above scrolls */}
            {canManageNotes && (
              <View style={[styles.notesModalInputBar, { flexDirection: rtl.rowDirection }]}>
                <TextInput
                  ref={notesInputRef}
                  style={[styles.notesModalInput, { textAlign: rtl.textStart }]}
                  value={newNoteContent}
                  onChangeText={setNewNoteContent}
                  placeholder={t('conversation.notesPlaceholder')}
                  placeholderTextColor={colors.textLight}
                  multiline
                  maxLength={500}
                />
                <TouchableOpacity
                  style={[styles.notesModalSendButton, !newNoteContent.trim() && styles.notesModalSendButtonDisabled]}
                  onPress={() => {
                    if (!newNoteContent.trim()) return;
                    handleAddNote(newNoteContent.trim());
                    setNewNoteContent('');
                    requestAnimationFrame(() => notesScrollRef.current?.scrollToEnd({ animated: true }));
                  }}
                  disabled={!newNoteContent.trim()}
                  activeOpacity={0.7}
                >
                  <Ionicons name="send" size={20} color={newNoteContent.trim() ? colors.primary : colors.textLight} />
                </TouchableOpacity>
              </View>
            )}
          </TouchableOpacity>
          </Animated.View>
        </TouchableOpacity>
      </Modal>

      <ForwardMessageModal
        visible={showForwardModal}
        onClose={() => {
          if (isForwarding) {
            return;
          }
          setShowForwardModal(false);
          setMessageToForward(null);
        }}
        sourceMessage={
          messageToForward
            ? {
                _id: messageToForward._id,
                messageId: messageToForward.messageId,
                externalId: messageToForward.externalId,
                type: messageToForward.type,
                content: messageToForward.content,
                mediaUrl: messageToForward.mediaUrl,
                mediaType: messageToForward.mediaType,
                mediaName: messageToForward.mediaName,
                mediaSize: messageToForward.mediaSize,
              }
            : null
        }
        currentConversationId={conversation?.conversationId || conversationId}
        isSubmitting={isForwarding}
        onConfirm={handleForwardConfirm}
      />

      {/* WhatsApp Contact Modal for Reopening Window */}
      <WhatsAppContactModal
        visible={showWhatsAppContactModal}
        onClose={() => setShowWhatsAppContactModal(false)}
        initialPhoneNumber={resolvedReopenPhone}
        initialContactName={conversation?.contactName || contact?.name || ''}
        autoStartConversation={true}
        onContactAdded={(contact) => {
          console.log('✅ [ConversationScreen] Contact updated');
          setShowWhatsAppContactModal(false);
          showNotification('success', t('conversation.contactUpdated'), t('conversation.contactUpdatedDesc', { name: contact.name }));
        }}
        onConversationStarted={(contact) => {
          console.log('✅ [ConversationScreen] Template message sent, window reopened');
          setShowWhatsAppContactModal(false);
          showNotification('success', t('conversation.windowReopened'), t('conversation.windowReopenedDesc'));
          loadConversation();
          loadMessages();
        }}
      />

      {!!shareType && <ShareDocumentModal
        visible={!!shareType}
        type={shareType}
        conversationId={conversationId}
        contactId={shareContactId}
        channel={conversation?.channel}
        language={customerLanguage()}
        onClose={() => setShareType(null)}
        onSent={() => { setSentVersion(v => v + 1); showNotification('success', t('common.success'), t('share.sent')); }}
        onCreateNew={() => { void prepareAiDocument(shareType === 'quotation' ? 'quotation' : 'order', []); }}
      />}
      {/* Custom template picker for reopening the 24-hour WhatsApp window */}
      <ReopenWithTemplateModal
        visible={showReopenTemplateModal}
        initialTemplateId={aiTemplateId}
        contactPhone={resolvedReopenPhone}
        contactName={conversation?.contactName || contact?.name || ''}
        onClose={() => setShowReopenTemplateModal(false)}
        onSent={() => {
          setShowReopenTemplateModal(false);
          showNotification('success', t('conversation.templateSent'), t('conversation.templateSentDesc'));
          loadConversation();
          loadMessages();
        }}
      />

      {/* Facebook Messenger: Reopen with message tag (same approach as WhatsApp template) */}
      <Modal
        visible={showFacebookReopenModal}
        transparent
        animationType="fade"
        onRequestClose={() => !sendingFacebookReopen && setShowFacebookReopenModal(false)}
      >
        <View style={styles.facebookReopenModalOverlay}>
          <View style={styles.facebookReopenModalContent}>
            <Text style={styles.facebookReopenModalTitle}>Reopen conversation</Text>
            <Text style={styles.facebookReopenModalSubtext}>
              Send a message to reopen the 24-hour window using Messenger's customer care tag. Your app may need message tag approval in Meta Business Suite first.
            </Text>
            <TextInput
              style={styles.facebookReopenModalInput}
              placeholder="Type your message..."
              placeholderTextColor={colors.text.secondary}
              value={facebookReopenMessage}
              onChangeText={setFacebookReopenMessage}
              multiline
              maxLength={1000}
              editable={!sendingFacebookReopen}
            />
            <View style={styles.facebookReopenModalActions}>
              <TouchableOpacity
                style={[styles.facebookReopenModalButton, styles.facebookReopenModalButtonCancel]}
                onPress={() => !sendingFacebookReopen && setShowFacebookReopenModal(false)}
                disabled={sendingFacebookReopen}
              >
                <Text style={styles.facebookReopenModalButtonCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.facebookReopenModalButton, styles.facebookReopenModalButtonSend]}
                onPress={async () => {
                  const text = (facebookReopenMessage || '').trim();
                  if (!text || sendingFacebookReopen) return;
                  setSendingFacebookReopen(true);
                  try {
                    const res = await apiService.sendConversationMessage(conversationId, {
                      content: text,
                      type: 'text',
                      messagingType: 'MESSAGE_TAG',
                      messageTag: 'human_agent',
                    });
                    if (res.statusCode === 1000) {
                      setShowFacebookReopenModal(false);
                      setFacebookReopenMessage('');
                      showNotification('success', t('conversation.messageSent'), t('conversation.messageSentDesc'));
                      loadConversation();
                      loadMessages();
                    } else {
                      showNotification('error', t('common.error'), res.errors?.[0]?.message || t('conversation.sendFailed'));
                    }
                  } catch (e: any) {
                    showNotification('error', t('common.error'), e?.message || t('conversation.sendFailed'));
                  } finally {
                    setSendingFacebookReopen(false);
                  }
                }}
                disabled={sendingFacebookReopen || !facebookReopenMessage.trim()}
              >
                {sendingFacebookReopen ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.facebookReopenModalButtonSendText}>Send</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );

  if (embedded) {
    // No <ScreenLayout> here at all — see innerContent's own doc comment.
    // flex:1/height:100% preserves the height chain RightPane's own
    // container already provides, which the message list's flex:1 sizing
    // below needs to resolve to a real height instead of 0.
    return (
      <View style={{ flex: 1, height: '100%' }}>
        {innerContent}
      </View>
    );
  }

  return (
    <ScreenLayout
      header={appHeader}
      showBottomTabs={false}
      onHeaderHeightChange={setAppHeaderHeight}
      showLeftPane={showLeftPane}
      onLeftPaneClose={() => {}}
      // Contacts list moved to the left pane — see ContactsListScreen.tsx's
      // own identical change for why (duplicate fetches when it lived in
      // the right pane) and LeftPane.tsx's RTL mirroring for why this is
      // what makes it show left in English / right in Arabic for free.
      leftPaneContent="contacts"
      showRightPane={showRightPane}
      onRightPaneClose={handleCloseRightPane}
      rightPaneContent={rightPaneContent}
      selectedContact={rightPaneContent === 'edit-contact' ? viewedContact : conversation}
      onContactSave={(updated: any) => {
        setViewedContact(updated);
        showNotification('success', t('common.success'), t('common.save'));
      }}
      onContactDelete={() => {
        setRightPaneContent('empty');
        setViewedContact(null);
      }}
    >
      {innerContent}
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: chat.wallpaper,
    position: 'relative',
    ...(Platform.OS === 'android' && {
      // Android: Ensure proper bottom padding for safe area
      paddingBottom: 0, // Let KeyboardAvoidingView handle this
    }),
    ...(Platform.OS === 'web' && {
      // On web, ensure the container has a fixed height to prevent full-page scrolling
      height: '100vh' as any,
      maxHeight: '100vh' as any,
      overflow: 'hidden' as any,
      paddingTop: '10px' as any,
      display: 'flex' as any,
      flexDirection: 'column' as any,
    }),
  },
  
  // Message Wrapper Styles - Enhanced for WhatsApp-like grouping
  messageWrapper: {
    position: 'relative',
    marginVertical: 4, // Increased spacing for better readability
    width: '100%',
  },
  messageWrapperOutbound: {
    alignSelf: 'flex-end',
    maxWidth: '100%',
  },
  messageWrapperInbound: {
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
  firstMessage: {
    marginTop: spacing.sm,
  },
  daySeparator: {
    alignSelf: 'center',
    backgroundColor: chat.in.bg,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 4,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: chat.in.border,
  },
  daySeparatorText: {
    fontSize: 12,
    fontWeight: '600',
    color: chat.in.subtle,
  },
  newGroupMessage: {
    marginTop: spacing.md,
  },

  // Enhanced Reply Context Styles
  replyContext: {
    backgroundColor: colors.background.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    ...(Platform.OS === 'web' && {
      borderTopWidth: 1,
      borderTopColor: colors.border,
    }),
  },
  
  // Input Container with Safe Area
  inputContainer: {
    ...(Platform.OS === 'web' && {
      // On web, fix the input at the bottom but respect panes. left/right are
      // set inline (physicalLeftInsetAnim/physicalRightInsetAnim) so they animate
      // in lockstep with the panes' own collapse/expand instead of snapping. This
      // outer element is just a positioning shell — inputContainerInner (below)
      // carries the visible background/border and caps its own width, matching
      // InboxScreen's content max-width so the input bar's border doesn't stick
      // to the browser edge when both panes are collapsed.
      position: 'fixed' as any,
      bottom: 0,
      zIndex: 1000,
      flex: 'none' as any,
    }),
  },
  // Embedded mode's version of `inputContainer` above — a normal, non-fixed
  // flex child sized to the embedding pane instead of the full browser
  // viewport, sitting at the bottom of the column in normal flex flow.
  inputContainerEmbedded: {
    position: 'relative',
  },
  inputContainerInner: {
    backgroundColor: chat.wallpaper,
    flexDirection: 'column',
    overflow: 'visible',
    ...(Platform.OS === 'web' && {
      maxWidth: 1400,
      marginHorizontal: 'auto' as any,
      width: '100%' as any,
    }),
  },
  replyIndicator: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: 'rgba(20, 99, 165, 0.08)',
    borderRadius: 12,
    padding: spacing.md,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
    borderWidth: 1,
    borderColor: 'rgba(20, 99, 165, 0.15)',
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  replyIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  replyContentContainer: {
    flex: 1,
  },
  replyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  replyLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.primary,
    flex: 1,
  },
  replyCancelButton: {
    padding: spacing.xs,
    borderRadius: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },
  replyContent: {
    fontSize: 14,
    color: colors.text.primary,
    lineHeight: 20,
    fontFamily: typography.fontFamily.regular,
  },

  // Modern Header Styles
  header: {
    position: 'relative',
    ...(Platform.OS === 'web' && {
      // On web, fix the header at the top but respect panes. left/right are set
      // inline (physicalLeftInsetAnim/physicalRightInsetAnim) so they animate in
      // lockstep with the panes' own collapse/expand instead of snapping; top
      // tracks appHeaderHeight (the real measured app-header height reported via
      // ScreenLayout's onHeaderHeightChange) so this header sits directly below
      // the app header with no dead gap, same as LeftPane/RightPane. This outer
      // element is just a positioning shell spanning the full space between the
      // panes — headerInner (below) carries the visible background/border and
      // caps its own width, matching InboxScreen's content max-width so the
      // chat's borders don't stick to the browser edge when both panes are
      // collapsed and the space between them exceeds that cap.
      position: 'fixed' as any,
      top: 84 as any,
      zIndex: 1000,
      flex: 'none' as any,
    }),
  },
  // Embedded mode's version of `header` above — a normal, non-fixed flex
  // child sized to the embedding pane instead of the full browser viewport.
  headerInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: designTokens.spacing.sm,
    backgroundColor: colors.background.surface,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
    ...(Platform.OS === 'web' && {
      maxWidth: 1400,
      marginHorizontal: 'auto' as any,
      width: '100%' as any,
    }),
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  headerCenter: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 0,
    flexShrink: 1,
  },
  
  // Avatar Styles
  avatarContainer: {
    position: 'relative',
  },
  
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    ...designTokens.shadows.sm,
  },
  
  avatarText: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.white,
    fontFamily: typography.fontFamily.medium,
  },
  
  avatarImage: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },
  
  channelBadge: {
    position: 'absolute',
    bottom: -10,
    right: -10,
    backgroundColor: colors.background.surface,
    borderRadius: 11,
    padding: 2,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 3,
  },
  
  // Contact Info Styles
  contactInfo: {
    flex: 1,
    justifyContent: 'center',
    minWidth: 0,
    flexShrink: 1,
  },
  
  contactName: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: 2,
  },
  
  contactPhone: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginBottom: 2,
  },
  
  onlineIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  
  onlineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#4CAF50',
  },
  
  onlineText: {
    fontSize: typography.fontSize.xs,
    color: '#4CAF50',
    fontFamily: typography.fontFamily.medium,
  },
  
  // Header Actions
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    flexShrink: 0,
  },
  
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: designTokens.spacing.sm,
    backgroundColor: String(colors.lightGray) + '40',
  },

  assignmentActionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    paddingHorizontal: 0,
    paddingVertical: 0,
    backgroundColor: 'transparent',
    borderBottomWidth: 0,
  },

  assignmentActionButton: {
    alignItems: 'center',
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: designTokens.spacing.sm,
    borderRadius: 999,
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: designTokens.spacing.sm,
  },

  assignmentActionButtonDanger: {
    borderColor: String(colors.error) + '30',
  },

  assignmentActionButtonDisabled: {
    opacity: 0.5,
  },

  groupActionsButton: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: chat.out.chip,
  },
  groupActionsIconWrap: {
    position: 'relative',
  },
  unseenNotesDot: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.error,
    borderWidth: 1,
    borderColor: colors.background.surface,
  },

  assignmentActionText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.medium,
    marginHorizontal: designTokens.spacing.xs,
  },

  assignmentActionTextDanger: {
    color: colors.error,
  },

  assignmentActionTextDisabled: {
    color: colors.text.disabled,
  },

  assignmentMenuOverlay: {
    flex: 1,
    backgroundColor: 'transparent',
  },

  assignmentMenu: {
    position: 'absolute',
    backgroundColor: colors.background.surface,
    borderRadius: 12,
    paddingVertical: designTokens.spacing.xs,
    minWidth: 180,
    borderWidth: 1,
    borderColor: colors.border,
    elevation: 3,
  },

  assignmentMenuItem: {
    alignItems: 'center',
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: designTokens.spacing.sm,
  },

  assignmentMenuItemDisabled: {
    opacity: 0.5,
  },

  assignmentMenuText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.medium,
  },
  assignmentMenuSubtext: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
    marginTop: 1,
  },

  assignmentMenuTextDisabled: {
    color: colors.text.disabled,
  },

  // Notes Modal
  notesModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  notesModalCard: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    minHeight: 320,
  },
  notesModalGrabber: {
    alignSelf: 'center',
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.border,
    marginTop: designTokens.spacing.sm,
  },
  notesModalHeader: {
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: designTokens.spacing.lg,
    paddingTop: designTokens.spacing.md,
    paddingBottom: designTokens.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  notesModalTitleRow: {
    alignItems: 'center',
    gap: designTokens.spacing.sm,
  },
  notesModalTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  notesModalCountBadge: {
    minWidth: 22,
    height: 22,
    paddingHorizontal: 6,
    borderRadius: 11,
    backgroundColor: String(colors.primary) + '15',
    alignItems: 'center',
    justifyContent: 'center',
  },
  notesModalCountText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.primary,
  },
  notesModalLoading: {
    padding: designTokens.spacing.xl,
    alignItems: 'center',
  },
  notesModalCardInner: {
    flex: 1,
  },
  notesModalScroll: {
    flexGrow: 0,
  },
  notesModalScrollContent: {
    padding: designTokens.spacing.lg,
    paddingBottom: designTokens.spacing.xl,
  },
  notesModalInputBar: {
    alignItems: 'flex-end',
    gap: designTokens.spacing.sm,
    paddingHorizontal: designTokens.spacing.lg,
    paddingTop: designTokens.spacing.sm,
    paddingBottom: designTokens.spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  notesModalInput: {
    flex: 1,
    maxHeight: 100,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
    backgroundColor: colors.background.secondary,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: designTokens.spacing.sm,
  },
  notesModalSendButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  notesModalSendButtonDisabled: {
    opacity: 0.5,
  },

  // Loading State
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.primary,
  },
  
  loadingText: {
    marginTop: designTokens.spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  
  // Near-Expiry Warning Banner
  nearExpiryBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFA72610',
    borderLeftWidth: 4,
    borderLeftColor: '#FFA726',
    paddingVertical: designTokens.spacing.sm,
    paddingHorizontal: designTokens.spacing.md,
    gap: designTokens.spacing.sm,
  },
  
  nearExpiryTextContainer: {
    flex: 1,
  },
  
  nearExpiryText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: '#F57C00',
    fontFamily: typography.fontFamily.semibold,
  },

  // Engagement Cooldown Banner
  cooldownBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E5393510',
    borderLeftWidth: 4,
    borderLeftColor: '#E53935',
    paddingVertical: designTokens.spacing.sm,
    paddingHorizontal: designTokens.spacing.md,
    gap: designTokens.spacing.sm,
  },

  cooldownText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: '#C62828',
    fontFamily: typography.fontFamily.semibold,
  },

  cooldownSubtext: {
    fontSize: typography.fontSize.xs,
    color: '#C62828',
    fontFamily: typography.fontFamily.regular,
  },
  
  nearExpiryCountdown: {
    fontSize: typography.fontSize.xs,
    color: '#F57C00',
    fontFamily: typography.fontFamily.medium,
    marginTop: 2,
  },

  
  loadingMoreContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: designTokens.spacing.md,
    backgroundColor: colors.background.primary,
  },

  loadingMoreText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  
  // Messages Container - Scrollable on web, flex on mobile
  messagesContainer: {
    flex: 1,
    backgroundColor: chat.wallpaper,
    ...(Platform.OS === 'web' && {
      // On web, make the messages area scrollable with fixed height. height/maxHeight
      // are set inline (see JSX) using the real measured app-header height instead of
      // a hardcoded guess. This element is just the scroll viewport — it stays full
      // width; messagesContainerInner (below) caps the actual content width so the
      // message list is centered the same way as the header/input bar instead of
      // stretching edge-to-edge when both panes are collapsed.
      overflow: 'auto' as any,
      flex: 'none' as any,
      marginTop: '80px' as any, // Add margin to account for fixed conversation header
      marginBottom: '120px' as any, // Add margin to account for fixed input
    }),
  },
  // Embedded mode's version of `messagesContainer` above — no fixed
  // header/input to clear, so this is just a plain scrollable flex:1 region
  // between the (now in-flow) contact-info bar and input bar.
  messagesContainerEmbedded: {
    flex: 1,
    backgroundColor: chat.wallpaper,
    ...(Platform.OS === 'web' && {
      overflow: 'auto' as any,
    }),
  },
  messagesContainerInner: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      maxWidth: 1400,
      marginHorizontal: 'auto' as any,
      width: '100%' as any,
      // Without this, a flex:1 box on web defaults to minHeight:'auto' and
      // grows to fit its content instead of being clamped to
      // messagesContainer's bounded/scrollable height — which means the
      // FlatList's own internal scroll node never actually overflows, so
      // the outer View ends up doing the scrolling instead. Since
      // onEndReached listens for scroll events on the FlatList's OWN node
      // (not an ancestor), it silently never fires on web — "load more"
      // works on native (RN Views don't scroll there regardless of nesting)
      // but not on web. minHeight:0 lets this box actually shrink to fit
      // its parent's bound, which is what makes the FlatList itself the
      // real scrolling element.
      minHeight: 0,
    }),
  },

  // Messages List
  messagesList: {
    flex: 1,
    backgroundColor: chat.wallpaper,
    ...(Platform.OS === 'web' && { minHeight: 0 as any }),
  },
  
  messagesContent: {
    paddingVertical: designTokens.spacing.md,
    flexGrow: 1,
    ...(Platform.OS === 'web' && {
      // Ensure content can scroll properly on web
      minHeight: '100%',
    }),
  },
  
  // Empty State
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: designTokens.spacing.xl,
    minHeight: 400,
  },
  
  emptyStateText: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginTop: designTokens.spacing.lg,
    marginBottom: designTokens.spacing.sm,
  },
  
  emptyStateSubtext: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    textAlign: 'center',
    lineHeight: 22,
  },
  
  // Typing Indicator
  typingIndicator: {
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: designTokens.spacing.sm,
    backgroundColor: colors.background.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },

  typingText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    fontStyle: 'italic',
  },

  // Conversation Closed State
  conversationClosedContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: designTokens.spacing.lg,
    backgroundColor: colors.background.secondary,
    borderRadius: designTokens.borderRadius.lg,
    marginHorizontal: designTokens.spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },

  conversationClosedIcon: {
    opacity: 0.7,
  },

  conversationClosedTextContainer: {
    flex: 1,
  },

  conversationClosedText: {
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.medium,
    marginBottom: 2,
  },

  conversationClosedSubtext: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
    lineHeight: 18,
  },

  reopenIconButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: String(colors.primary) + '15',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: designTokens.spacing.sm,
  },
  templateInputBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: 12,
    backgroundColor: colors.background.secondary,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
  },
  templateInputBarText: {
    flex: 1,
    fontSize: 15,
    color: colors.text.tertiary,
    fontFamily: typography.fontFamily.regular,
  },
  facebookReopenModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  facebookReopenModalContent: {
    backgroundColor: colors.background.primary,
    borderRadius: 12,
    padding: spacing.lg,
    width: '100%',
    maxWidth: 400,
  },
  facebookReopenModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  facebookReopenModalSubtext: {
    fontSize: 14,
    color: colors.text.secondary,
    marginBottom: spacing.md,
  },
  facebookReopenModalInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: spacing.sm,
    fontSize: 16,
    color: colors.text.primary,
    minHeight: 80,
    textAlignVertical: 'top',
    marginBottom: spacing.md,
  },
  facebookReopenModalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
  },
  facebookReopenModalButton: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    minWidth: 80,
    alignItems: 'center',
  },
  facebookReopenModalButtonCancel: {
    backgroundColor: colors.background.secondary,
  },
  facebookReopenModalButtonCancelText: {
    color: colors.text.secondary,
    fontWeight: '600',
  },
  facebookReopenModalButtonSend: {
    backgroundColor: colors.primary,
  },
  facebookReopenModalButtonSendText: {
    color: '#fff',
    fontWeight: '600',
  },

  // Active Assignment Overlay
  activeAssignmentOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },

  activeAssignmentFullOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 20,
    pointerEvents: 'auto' as const,
  },

  activeAssignmentAbsoluteOverlay: {
    position: 'absolute',
    top: Platform.OS === 'web' ? '80px' : 72,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
    pointerEvents: 'auto' as const,
  },

  activeAssignmentContent: {
    alignItems: 'center',
    gap: spacing.md,
  },

  activeAssignmentText: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.white,
    fontFamily: typography.fontFamily.semibold,
  },

  activeAssignmentSubtext: {
    fontSize: typography.fontSize.sm,
    color: 'rgba(255, 255, 255, 0.9)',
    fontFamily: typography.fontFamily.regular,
  },
});
